/**
 * The ONLY place user-facing strings live in this app. Every label, button, heading, and
 * fallback error message a component renders comes from here — a string literal rendered
 * directly in a component is a defect. Server-sent error messages (e.g. from `ApiError`)
 * are the one exception: those come from the API response itself, not this dictionary.
 *
 * THE PRODUCT NAME IS NEVER IN THIS FILE. Branding (name, tagline, supporting line, glyph,
 * logo) comes from `GET /api/branding` at runtime — R2. Any sentence that needs to say the
 * product's name is a function taking that name as an argument.
 */

/**
 * The two appearance axes, named once. Both the group labels and the accessible-name
 * builders in `appearance` below read from here, so the axis wording can never drift
 * between the two — the same rule the rest of this file follows, applied inside it: a
 * string is written once and referenced, never restated.
 */
const AXIS = {
  palette: 'Color palette',
  mode: 'Mode',
} as const;

/** Count + noun, singular or plural — the access module's strings share it. */
const accessPlural = (n: number, one: string, many: string): string =>
  `${String(n)} ${n === 1 ? one : many}`;

/** One noun per Queue type, singular and plural: the tiles, the lanes and the strip agree. */
const ACCESS_TYPE_NOUNS = {
  request: { one: 'request', many: 'Requests' },
  needs_access: { one: 'needs access', many: 'Needs access' },
  requested: { one: 'requested', many: 'Requested' },
  remove_access: { one: 'remove access', many: 'Remove access' },
} as const;

export const STR = {
  common: {
    loading: 'Loading…',
    cancel: 'Cancel',
    back: 'Back',
    continue: 'Continue',
    close: 'Close',
    understood: 'Understood',
    saveChanges: 'Save changes',
    search: 'Search',
    clear: 'Clear',
    clearSearch: 'Clear search',
    noMatch: 'No match.',
    /** The design of record puts this in the page header, beside the primary action. */
    logOut: 'Log out',
    loggingOut: 'Logging out…',
    signedInAs: 'Signed in as',
    revoke: 'Revoke',
    dash: '—',
    /** A read genuinely failed — offered wherever a failure is shown instead of content. */
    tryAgain: 'Try again',
    /**
     * Admin lists load a capped page this round, so a list can be shorter than the
     * collection it is drawn from. A partial list must never read as a whole one — and the
     * search boxes on these screens filter what is loaded, so the notice says that too
     * rather than implying the missing rows are one search away. Both numbers are real:
     * the rows in hand and the server's own total.
     */
    listTruncated: (loaded: number, total: number): string =>
      `Only the first ${String(loaded)} of ${String(total)} are loaded. The search above looks at those alone; paging through the rest comes in a later round.`,
  },

  errors: {
    generic: 'Something went wrong. Please try again.',
    network: 'Could not reach the server. Check your connection and try again.',
    /** Shown in place of a surface when a read fails for a reason we cannot name. */
    couldNotLoad: 'Could not load this. Try again in a moment.',
  },

  /**
   * ONE appearance control, so ONE set of labels. The two axis names live once, in the
   * module-level `AXIS` const above, and reach a screen only through the `…Label` builders
   * below — there are deliberately no standalone `…Group` keys for them any more, because
   * nothing renders an axis name on its own. The `…Short` four are what the buttons PRINT —
   * the CURRENT value of each axis, in the admin demo's short all-caps form.
   *
   * The `…Label` pair are the buttons' accessible names, and they exist because those
   * buttons carry their selected treatment AT REST: each one shows a value and cycles it
   * rather than toggling on and off, so it has no pressed state to announce. The name has
   * to carry both halves of what the screen shows — which axis, and what it is currently
   * set to — or the state made visible in this round would still be invisible to a screen
   * reader. `…Name…` are the long forms those names spell the value out in: "SAGE" is a
   * legible chip and an illegible thing to hear.
   */
  appearance: {
    paletteShortDefault: 'DEFLT',
    paletteShortSage: 'SAGE',
    modeShortLight: 'LIGHT',
    modeShortDark: 'DARK',
    paletteNameDefault: 'Default',
    paletteNameSage: 'Sage',
    modeNameLight: 'Light',
    modeNameDark: 'Dark',
    paletteLabel: (value: string): string => `${AXIS.palette}: ${value}`,
    modeLabel: (value: string): string => `${AXIS.mode}: ${value}`,
  },

  /**
   * The ten announcement swatches, spoken. The SET itself is `ANNOUNCEMENT_SWATCHES` in
   * `@accounts/shared` — imported, never restated in /web — and each value IS the CSS class
   * suffix that paints it (`c-amber` … `c-slate`). This map exists only so a 22px color
   * button has an accessible name and a tooltip; it is a label table, never a color table,
   * and nothing renders a color from it.
   */
  swatchNames: {
    amber: 'Amber',
    blue: 'Blue',
    emerald: 'Emerald',
    red: 'Red',
    orange: 'Orange',
    violet: 'Violet',
    indigo: 'Indigo',
    fuchsia: 'Fuchsia',
    rose: 'Rose',
    slate: 'Slate',
  },

  /**
   * The "no color" slot's name, used ONLY where the strip is nullable (today: an
   * application's pill color). It is a real, selectable choice — an application may
   * legitimately have none, and it then renders uncolored wherever it appears — so it is
   * named like every other swatch rather than described as an absence.
   */
  swatchNone: 'No color',

  /* ------------------------------------------------------------------ sign-in -- */

  login: {
    title: 'Sign in',
    emailLabel: 'Email address',
    emailPlaceholder: 'name@20free.net',
    passwordLabel: 'Password',
    submit: 'Sign in',
    submitting: 'Signing in…',
    /**
     * Rendered when ?redirect= names an application THE SERVER RECOGNISED. The name comes
     * from the server; there is deliberately no generic form of this sentence. When the
     * target is unregistered, inactive, unparseable, or the lookup fails, the notice is
     * absent entirely — telling somebody they will continue where they left off when they
     * are about to land on the Accounts root is a claim this app cannot back.
     */
    continueTo: 'You’ll continue to',
    continueToSuffix: 'after signing in.',
    signedOutNotice: 'You’ve been signed out.',
    /**
     * C3 — the server names WHY a live session was refused, but only to a caller whose cookie
     * unsigned validly for a real account. So each of these is reachable only by somebody who
     * already held a session this service issued, and none of them can be provoked by an
     * anonymous caller testing whether an address exists.
     */
    sessionSignedOutElsewhere:
      'An administrator ended your sessions. You can sign straight back in.',
    sessionPasswordChanged:
      'Your password changed, so your existing sessions ended. Sign in with your new password.',
    sessionEnded: 'Your session ended. Sign in again to continue.',
    /** The suite session is valid but the administrator elevation is missing or stale. */
    sessionElevationRequired: 'Sign in again to use administrator functions.',
    /** No reason came back, but this client knew it held a session — so it lapsed. */
    sessionExpired: 'Your session expired. Sign in again to continue.',
    /**
     * R4 — provenance decides the message. An anonymous caller with bad credentials gets
     * this and only this: identical for an unknown address, a wrong password and a
     * deactivated account, so it can never be used to test whether an address exists.
     */
    badCredentials: 'Email or password is incorrect.',
    missingFields: 'Email and password are both required.',
    rateLimited: 'Too many attempts — try again in a few minutes.',
    /**
     * R4 — the other message. Shown only when a LIVE session was refused, which the client
     * knows because it held one and lost it. Never reachable from an anonymous attempt, so
     * it leaks nothing about which addresses exist.
     */
    sessionInactive: 'Your account is no longer active — contact your administrator.',
    aux: 'Accounts are created by an administrator. If you are locked out, an administrator can reset your password.',
  },

  changePassword: {
    title: 'Choose a new password',
    lead: 'You signed in with a temporary password. Choose your own before continuing.',
    currentPasswordLabel: 'Temporary password',
    newPasswordLabel: 'New password',
    confirmPasswordLabel: 'Confirm new password',
    lengthHint: 'At least 12 characters.',
    submit: 'Set password and continue',
    submitting: 'Setting password…',
    mismatch: 'The two passwords do not match.',
    tooShort: 'Use at least 12 characters.',
  },

  /* ------------------------------------------------------- two-step (2FA) ----- */

  /**
   * The 2FA wave's strings, from `reference/accounts-2fa-demo.html` — the frozen design of
   * record. The demo's copy is carried VERBATIM wherever the demo draws the screen; every
   * server refusal that reaches an eye here is a full sentence, never a wire code.
   */
  twoFactor: {
    /* ---- Enrolment — scan & confirm (the demo's design of record) ---- */
    setup: {
      title: 'Set up two-step verification',
      lead: 'Scan the code with an authenticator app — Google Authenticator, 1Password, and so on. One extra step at sign-in means a stolen password alone can’t get in, in any application in the suite.',
      /** The QR's accessible name — what the image is FOR, for somebody who cannot see it. */
      qrLabel: 'QR code for your authenticator app',
      manualEntry: 'Or enter the key by hand:',
      copyKey: 'Copy',
      /** The otpauth link row — the same key plus its settings, for apps that take a link. */
      linkEntry: 'Or paste the set-up link:',
      copyLink: 'Copy link',
      keyCopied: 'Set-up key copied.',
      linkCopied: 'Set-up link copied.',
      codeLabel: 'Then enter the six-digit code it shows',
      submit: 'Turn on two-step',
      checking: 'Checking…',
      /** The demo's wrong-code sentence — why it failed and what to do, no jargon. */
      wrongCode: 'That code didn’t work. Codes change every 30 seconds — check your app and try again.',
      emptyCode: 'Enter the six-digit code from your app.',
      /** Loading the secret failed for a reason the server named — its sentence renders. */
      beginFailed: 'Could not start set-up. Try again in a moment.',
      tryAgain: 'Try again',
      /* ---- Recovery codes — shown once, RULED COPY ---- */
      codesTitle: 'Save your recovery codes',
      codesLead:
        'Two-step is confirmed. If you lose your phone, one of these codes signs you in instead — each works exactly once.',
      codesOnce:
        'This is the only time these codes are shown. Once you leave this screen they cannot be viewed again — only replaced.',
      finish: 'Finish set-up',
      /* ---- Done ---- */
      doneTitle: 'Two-step is on',
      doneLead:
        'From now on, signing in asks for your password and then a code from your app — in every application in the suite.',
      viewInAccount: 'View it in your account',
      aux: 'You can turn two-step off or regenerate recovery codes from your account at any time.',
      /** The intermediate sign-in step lapsed — there is nothing to enrol against any more. */
      expired: 'Your sign-in step expired. Start again from the sign-in page.',
      backToSignIn: 'Back to sign in',
    },

    /* ---- The sign-in challenge ---- */
    challenge: {
      title: 'Two-step verification',
      leadTotp: 'Enter the six-digit code from your authenticator app.',
      leadRecovery: 'Enter one of the recovery codes you saved when you set up two-step.',
      codeLabel: 'Six-digit code',
      recoveryLabel: 'Recovery code',
      recoveryHint: 'Each recovery code works exactly once. Codes were shown when you set up two-step.',
      submitTotp: 'Verify',
      submitRecovery: 'Use recovery code',
      verifying: 'Verifying…',
      swapToRecovery: 'Use a recovery code instead',
      swapToTotp: 'Use your authenticator app instead',
      backToSignIn: 'Back to sign in',
      wrongCode: 'That code didn’t work. Codes change every 30 seconds — check your app and try again.',
      emptyCode: 'Enter the six-digit code from your app.',
      wrongRecovery:
        'That recovery code isn’t valid. Each code works exactly once — check you haven’t already used this one.',
      emptyRecovery: 'Enter one of your recovery codes.',
      rateLimited: 'Too many attempts — try again in a few minutes.',
      /** The pending step lapsed (closed laptop, long pause) — sign in again from the top. */
      expired: 'Your sign-in step expired. Go back and sign in again.',
      aux: 'Lost your phone and your recovery codes? An administrator can reset two-step for you.',
      /** After a recovery-code sign-in — how many remain, said while it still matters. */
      codesRemaining: (count: number): string =>
        count === 1
          ? '1 recovery code remains.'
          : `${String(count)} recovery codes remain.`,
    },

    /* ---- The Security surface (self-service management) ---- */
    security: {
      back: '← Your applications',
      title: 'Security',
      subtitle: (name: string, email: string): string =>
        `Two-step verification for ${name} · ${email}.`,
      sectionLabel: 'Two-step verification',
      rowTitle: 'Two-step verification',
      chipOn: 'Two-step on',
      chipOff: 'Not set up',
      enrolledNote: (date: string): string =>
        `Enrolled ${date} · an authenticator app protects every application in the suite.`,
      /** Enabled, but the enrolment date could not be read — claim only what is known. */
      enabledNote: 'An authenticator app protects every application in the suite.',
      offNote:
        'One extra step at sign-in — a six-digit code from an app on your phone — so a stolen password alone can’t get in. Enroll once and every application in the suite is covered.',
      turnOn: 'Turn on two-step',
      codesRowTitle: 'Recovery codes',
      codesLeftLine: (remaining: number): string =>
        remaining === 1 ? '1 of 10 codes remains.' : `${String(remaining)} of 10 codes remain.`,
      codesLeftSuffix: 'Each signs you in exactly once if you lose your phone.',
      regenerate: 'Regenerate codes',
      turnOffRowTitle: 'Turn off two-step',
      turnOffNote:
        'Every application stops asking for a second step. Your password alone will sign you in.',
      turnOff: 'Turn off two-step',
    },

    /* ---- Modals on the Security surface ---- */
    regenModal: {
      title: 'Regenerate recovery codes?',
      lead: 'Ten new codes replace your current set.',
      warning:
        'Your old codes stop working the moment new ones are made. Anywhere you’ve stored the old set becomes out of date.',
      passwordLabel: 'Confirm your current password',
      passwordRequired: 'Enter your current password.',
      submit: 'Regenerate codes',
      working: 'Regenerating…',
    },
    newCodesModal: {
      title: 'Your new recovery codes',
      lead: 'The old set is now invalid. Each of these signs you in exactly once.',
      once: 'This is the only time these codes are shown. Once you close this they cannot be viewed again — only replaced.',
      done: 'Done',
    },
    offModal: {
      title: 'Turn off two-step?',
      body: 'Every application in the suite stops asking for a second step — your password alone will sign you in. Your recovery codes stop working too. You can turn two-step back on at any time.',
      warning: 'Your account loses its strongest protection.',
      passwordLabel: 'Confirm your current password',
      passwordRequired: 'Enter your current password.',
      submit: 'Turn off two-step',
      working: 'Turning off…',
    },

    /* ---- Recovery-code panel (shared by set-up and regeneration) ---- */
    codes: {
      copyAll: 'Copy all',
      downloadTxt: 'Download .txt',
      ackTitle: 'I’ve saved these codes somewhere safe',
      ackNote: 'A password manager is ideal. Anyone who finds them can use them.',
      copied: (count: number): string =>
        count === 1 ? 'The code was copied.' : `All ${String(count)} codes copied.`,
      copyFailed: 'Couldn’t copy — select the text by hand.',
      downloaded: 'Recovery codes downloaded.',
      downloadFailed: 'Couldn’t start the download here.',
      /** The .txt file's own lines. The product name is an argument, never a literal (R2). */
      fileHeader: (brand: string, email: string): string => `${brand} — recovery codes for ${email}`,
      fileGenerated: (date: string): string =>
        `Generated ${date}. Each code signs you in exactly once.`,
      fileName: 'recovery-codes.txt',
    },
  },

  /* ------------------------------------------------------------------ sessions -- */

  /**
   * Shared by both session surfaces — the admin Sessions screen and the self-service Active
   * sessions section on Security. THE META LINE ruling (2026-08-08): the two frozen demos
   * predate geolocation and each showed half of it (self-service showed a location and no
   * address; admin showed an address and no location) — the operator ruled that both now
   * render, adjacent, on both surfaces. `locationApprox` is RENDER-side copy only: the server
   * never stores the "(approximate)" hedge, and there is deliberately no state beyond "known"
   * and `locationUnknown` — no "local network", no per-range label.
   */
  sessions: {
    thisDevice: 'This device',
    signOut: 'Sign out',
    locationUnknown: 'Location unknown',
    locationApprox: (location: string): string => `${location} (approximate)`,
    signedInRelative: (relative: string): string => `signed in ${relative}`,
    /** Self-service only — the admin surface says this by showing "Unknown device" itself. */
    deviceUnknownClause: 'the browser didn’t identify itself',
    deviceWord: (count: number): string => (count === 1 ? '1 device' : `${String(count)} devices`),

    /* ---- the admin Sessions screen (reference/accounts-sessions-admin-demo.html) ---- */
    admin: {
      searchPlaceholder: 'Search name or email',
      expandAll: 'Expand all',
      collapseAll: 'Collapse all',
      devicesTooltipHeading: 'Devices',
      appsTooltipHeading: 'Last seen in',
      /** The devices tooltip lists every device name, suffixing the viewer's own. */
      deviceTooltipName: (label: string, current: boolean): string =>
        current ? `${label} (this device)` : label,
      devicesAriaLabel: (names: string): string => `Devices: ${names}`,
      appsAriaLabel: (names: string): string => `Apps in use: ${names}`,
      appWord: (count: number): string => (count === 1 ? '1 app' : `${String(count)} apps`),
      /**
       * The filter bar's result line. THE CARVE-OUT: the apps segment is dropped entirely at
       * zero — it must never read "0 apps" — and the two zero/no-match forms never claim a
       * device or app count that has not been established.
       */
      count: (visiblePeople: number, totalPeople: number, devices: number, apps: number): string => {
        if (totalPeople === 0) return '0 people';
        if (visiblePeople === 0) return `0 of ${String(totalPeople)} people`;
        const people = visiblePeople === 1 ? '1 person' : `${String(visiblePeople)} people`;
        const deviceWord = devices === 1 ? '1 device' : `${String(devices)} devices`;
        const base = `${people} · ${deviceWord}`;
        return apps === 0 ? base : `${base} · ${apps === 1 ? '1 app' : `${String(apps)} apps`}`;
      },
      groupToggleLabel: (name: string, deviceWord: string, expanded: boolean): string =>
        `${name} — ${deviceWord}${expanded ? ', collapse' : ', expand'}`,
      /** THE CARVE-OUT's per-row meta clause — appended only when `apps` is non-empty. */
      lastSeenIn: (names: string): string => `last seen in ${names}`,
      emptyTitle: 'Nobody is signed in',
      emptyBody:
        'Live sessions appear here the moment someone signs in, and leave the moment they end.',
      noMatch: 'Nobody matches that search.',
      /** The group row's own action AND the confirm modal's submit button — one literal. */
      signOutOfAll: 'Sign out of all',
      deviceModal: {
        title: (name: string): string => `Sign ${name} out of this device?`,
        /**
         * ONE composed sentence, never fragments — `othersRemaining` is the person's OTHER
         * live device count (their total minus this one), and `address` is null-safe: the
         * parenthetical is omitted rather than printing a placeholder.
         */
        body: (deviceLabel: string, address: string | null, othersRemaining: number): string => {
          const target = address === null ? deviceLabel : `${deviceLabel} (${address})`;
          const others =
            othersRemaining === 0
              ? ''
              : othersRemaining === 1
                ? ' Their other device stays signed in.'
                : ' Their other devices stay signed in.';
          return `${target} stops being signed in immediately — that device only, in every application.${others} Their password and access stay as they are.`;
        },
        submit: 'Sign out this device',
      },
      allModal: {
        title: (name: string): string => `Sign ${name} out everywhere?`,
        body: (deviceWord: string): string =>
          `They are signed in on ${deviceWord}. Ends every signed-in device at once, in every application. Their password and access stay as they are.`,
        /** Shown only when the target IS the viewer's own account. */
        selfNote:
          'That includes the session you are using right now — you will land back on the sign-in screen.',
      },
    },

    /* ---- the self-service Active sessions section (reference/accounts-2fa-demo.html) ---- */
    security: {
      sectionLabel: 'Active sessions',
      honesty:
        'Device names and locations are best guesses from each browser and its network address — city-level at most, and sometimes wrong.',
      signOutOthers: 'Sign out everywhere else',
      singleDevice: 'You’re only signed in on this device.',
      othersModal: {
        title: 'Sign out everywhere else?',
        body: (others: number): string =>
          `${others === 1 ? '1 other device stops' : `${String(others)} other devices stop`} being signed in immediately, in every application. This device stays signed in. Your password is unchanged.`,
        submit: 'Sign out everywhere else',
      },
    },
  },

  /* ----------------------------------------------------------------- launcher -- */

  launcher: {
    /**
     * Time of day plus a first name, modelled as data rather than assembled in the component
     * — a greeting built by concatenating fragments is a sentence no translator can reorder.
     * `format.ts` picks the part of day; this is the only place the wording lives.
     */
    greeting: {
      morning: (firstName: string): string => `Good morning, ${firstName}.`,
      afternoon: (firstName: string): string => `Good afternoon, ${firstName}.`,
      evening: (firstName: string): string => `Good evening, ${firstName}.`,
      /** No name to greet by — still a greeting, never a blank or a placeholder name. */
      anonymous: {
        morning: 'Good morning.',
        afternoon: 'Good afternoon.',
        evening: 'Good evening.',
      },
    },
    sectionLabel: 'Applications',
    /**
     * The second section, from the launcher design of record. The section renders only when
     * there is at least one ACTIVE announcement to show — no announcements means the region
     * is simply absent, never an empty box claiming there is nothing.
     */
    announcementsSection: 'Announcements',
    /**
     * A long announcement clamps to three lines behind this, which opens the whole thing —
     * pills and all — in a dialog. The body it opens is the SERVER's rendered HTML, the same
     * bytes the clamped card shows; nothing is re-rendered client-side.
     */
    viewMore: 'View more',
    viewMoreLabel: 'View the full announcement',
    /** The reader dialog's accessible name when the announcement has no title of its own. */
    announcementDialog: 'Announcement',
    /** Quiet, beside the name. Absent entirely when the application reported no version. */
    version: (version: string): string => `v${version}`,
    open: 'Open',
    openAnyway: 'Open anyway',
    tryAnyway: 'Try anyway',
    /** The launch control's accessible name — the action plus which application it opens. */
    launchLabel: (action: string, application: string): string => `${action} ${application}`,
    maintenanceNote: 'Planned downtime right now — it should be back shortly.',
    unreachableNote: 'Not responding right now — opening it likely will not work.',
    /** Warm and factual. Nothing here may read as a fault: no grants is not an error. */
    emptyTitle: 'No applications yet',
    emptyBody:
      'Your administrator grants access. Once they do, everything you can open appears here — nothing is needed from you.',
    checkAgain: 'Check again',
    account: {
      menuLabel: 'Account menu',
      adminPanel: 'Admin panel',
      /** The way to the Security surface — two-step verification management. */
      security: 'Security',
      changePassword: 'Change password',
      signOut: 'Sign out',
      signOutEverywhere: 'Sign out everywhere',
      appearance: 'Appearance',
      /**
       * THE LAYOUT SELECTOR — the one addition to this menu, and it sits BELOW the palette
       * and mode buttons, which are unchanged. It is a SELECTOR, not a cycle button: both
       * options are always visible and the one in use is filled and check-marked, which is
       * what the helper line below states in words as well.
       *
       * This preference is PER-USER and SERVER-SIDE (it follows the person across
       * browsers) — deliberately unlike palette and mode, which stay per-device.
       */
      layout: 'Layout',
      layoutGroup: 'Launcher layout',
      layoutTiles: 'Tiles',
      layoutList: 'List',
      layoutHint: 'Pick one — the filled option is in use.',
    },
  },

  /* ------------------------------------------------------------------ refusals -- */

  refusal: {
    title: 'You do not have access to this screen',
    body: 'This screen is for administrators. If you think you should be able to open it, ask an administrator to grant you the role.',
    back: 'Back to your account',
  },

  notFound: {
    title: 'That screen does not exist',
    body: 'The address you opened is not part of this application.',
    back: 'Back to your account',
  },

  /* ------------------------------------------------------------------ surfaces -- */

  shell: {
    sections: 'Sections',
    /** The rail's brand mark is a control: it goes to the first screen of the panel. */
    home: 'Home — Applications',
    /**
     * The page header's way out of the panel and back to the launcher. Everybody lands on
     * the launcher after signing in, administrators included, so the panel needs a declared
     * route back rather than leaving an administrator to type the address.
     */
    launcher: 'Launcher',
    reorder: 'Drag to reorder',
    /** The rail's avatar opens the edit dialog for the signed-in person. */
    editSelf: (name: string): string => `${name} — edit your profile`,
    /** Read with the Access entry when its red badge shows (the badge itself is hidden). */
    waitingRequests: (n: number): string =>
      n === 1 ? ', 1 request waiting' : `, ${String(n)} requests waiting`,
  },

  surfaces: {
    users: {
      navLabel: 'Users',
      navLetter: 'U',
      title: 'Users',
      subtitle: 'Everyone who can sign in to the suite.',
      primary: 'Create user',
    },
    applications: {
      navLabel: 'Applications',
      navLetter: 'A',
      title: 'Applications',
      subtitle: (brand: string): string =>
        `Applications registered with ${brand}, and the status each one reports.`,
      primary: 'Register application',
    },
    grants: {
      navLabel: 'Grants',
      navLetter: 'G',
      title: 'Access grants',
      subtitle: 'Who may enter which application.',
    },
    /** The Access hub (SIR-7424, FINAL demo R3-2): one rail entry, its sections inside. */
    access: {
      navLabel: 'Access',
      navLetter: 'X',
      title: 'Access',
      subtitle: 'Requests, setup and removal work, the catalog, and who has what.',
      queueSubtitle: 'Requests and access work waiting for someone, oldest first.',
      catalogSubtitle: 'What people can have, how it is filed, and who can give it.',
      matrixSubtitle: 'Every catalog row against every person, filed by your hierarchy.',
      itEmailsSubtitle: 'The work sent to IT for systems outside the suite, and what is left.',
      emailsSubtitle: 'Every answer Accounts emails to the person who asked, newest first.',
    },
    audit: {
      navLabel: 'Audit log',
      navLetter: 'L',
      title: 'Audit log',
      /**
       * NO DATE. The design of record prints "since the log went live on 14 Jul 2026", but
       * this header is a pure function of the brand name — it has no row to read a date from,
       * and the oldest entry ON SCREEN is the oldest MATCHING the current filters, which is a
       * different fact entirely. So the sentence says the same true thing without a number
       * nothing can back: the log starts where auditing started, and a short history is
       * expected rather than a sign that nothing happened.
       */
      subtitle:
        'Every administrative and security event since auditing went live. Earlier activity was not recorded, so a short history is expected.',
    },
    sessions: {
      navLabel: 'Sessions',
      navLetter: 'Se',
      title: 'Sessions',
      subtitle:
        'Everyone signed in across the suite right now — and the means to end any of it.',
    },
    announcements: {
      navLabel: 'Announcements',
      navLetter: 'N',
      title: 'Announcements',
      subtitle: 'Notices shown to people across the suite.',
      primary: 'Post announcement',
    },
    settings: {
      navLabel: 'Settings',
      navLetter: 'S',
      title: 'Settings',
      subtitle: (brand: string): string => `How ${brand} presents itself across the suite.`,
      operationsSubtitle: 'Health polling, sessions, two-step and the rate limits.',
      emailSubtitle: 'Whether email is sent, who it comes from, and which events send it.',
      accessSubtitle: 'How the access work is timed, and what HR and IT can see.',
    },
  },

  /* The not-yet-designed chip is GONE with the card that wore it: Settings' "later" card
     became the real Rate limits card (U5), so no surface in this app is a placeholder any
     more. The placeholder SCREEN had already retired in Wave 3B. */

  /* ------------------------------------------------------------- access hub -- */

  /** The hub frame only; each section's own strings sit under `access` further down. */
  accessHub: {
    sections: 'Access sections',
    overview: 'Overview',
    queue: 'Queue',
    catalog: 'Catalog',
    matrix: 'Matrix',
    itEmails: 'IT emails',
    emails: 'Emails',
    /** The sub-nav's red count of requests waiting (spec row 14). */
    requestsWaiting: (n: number): string =>
      n === 1 ? '1 request waiting' : `${String(n)} requests waiting`,
    /** The sub-nav's count beside Queue. */
    waiting: (n: number): string => `${String(n)} waiting`,
    overdueSome: (n: number): string => `${String(n)} waiting, some overdue`,
  },

  /** Shared by every access screen, so a status reads the same wherever it is shown. */
  accessCommon: {
    recordStatus: {
      active: 'Active',
      needs_access: 'Needs access',
      requested: 'Requested',
      remove_access: 'Remove access',
      removed: 'Removed',
    },
    /** The Matrix's and the Grid's short cell words (the tracker's own vocabulary). */
    recordCell: {
      needs_access: 'NEEDS',
      requested: 'REQUESTED',
      remove_access: 'REMOVE',
      removed: 'Removed',
    },
    requestStatus: {
      pending: 'Requested',
      approved: 'Approved',
      denied: 'Denied',
      cancelled: 'Cancelled',
    },
    passwordHandling: {
      managed: 'Managed',
      given_raw: 'Given raw',
    },
    passwordHandlingHint: {
      managed: 'Saved for them in the VM. They never see the password.',
      given_raw: 'They were told the password.',
    },
    /** A catalog row with no Connected app. */
    outsideSystem: 'None (outside system)',
    notPlaced: 'Not filed',
    pathSeparator: ' · ',
    noLogin: 'No login yet',
  },

  /* -------------------------------------------------------------- IT emails -- */

  /** Spec row 15: New email to IT, the IT emails page and the Waiting on IT lane. */
  itEmails: {
    /* ---- the two tones; the words always ride beside the colour ---- */
    setUp: 'Set up',
    remove: 'Remove',
    legendSetUp: 'IT creates the login, or adds them to it',
    legendRemove: 'IT removes it, or rotates the password',
    countSetUp: (n: number): string => `${String(n)} set up`,
    countRemove: (n: number): string => `${String(n)} remove`,
    countSeparator: ' · ',

    /* ---- step 1: pick ---- */
    newEmail: 'New email to IT',
    pickLead: 'Choose what goes in one email. You can edit the text on the next step.',
    pickHelp:
      'Open setup and removal work in outside systems that is not with IT yet. Tick a system to take all of it, or pick people.',
    pickEmpty: 'Nothing is waiting to go to IT.',
    pickSystem: (system: string): string => `All of ${system}`,
    pickOne: (word: string, person: string): string => `${word}, ${person}`,
    pickedLine: (n: number): string => (n === 1 ? '1 item picked:' : `${String(n)} items picked:`),
    noLoginYet: 'no login yet',
    draftEmail: 'Draft email',
    draftWith: (n: number): string => `Draft email with ${String(n)}`,
    draftWithSplit: (n: number, setUp: number, remove: number): string =>
      `Draft email with ${String(n)} (${String(setUp)} set up, ${String(remove)} remove)`,
    drafting: 'Drafting…',

    /* ---- step 2: the email ---- */
    draftTitle: 'Draft email to IT',
    sentTitle: 'Email to IT',
    draftLead:
      'One email, grouped by system and then by person. Never a password: shared logins say "rotate", and IT shares the new password directly.',
    summaryHead: (items: number, systems: number): string =>
      `${accessPlural(items, 'item', 'items')} in ${accessPlural(systems, 'system', 'systems')}:`,
    toSetUp: (n: number): string => `${String(n)} to set up`,
    toRemove: (n: number): string => `${String(n)} to remove`,
    summaryJoin: ', ',
    summaryShared: (n: number): string =>
      n === 1
        ? '1 item is on a shared login; the email names who else uses it.'
        : `${String(n)} items are on a shared login; the email names who else uses it.`,
    emailText: 'Email text',
    editText: 'Edit text',
    doneEditing: 'Done editing',
    editingHint: 'Plain text while you edit; the colours come back when you press Done editing.',
    sendHint:
      'Accounts does not send email to IT. Copy the text into your mail app and send it, then press Mark as sent so the items move to Waiting on IT. Save draft keeps it for later.',
    saveDraft: 'Save draft',
    copyText: 'Copy text',
    copied: 'Email text copied. Paste it into your mail app.',
    copyFailed: 'Could not copy. Select the text and copy it yourself.',
    markSent: (n: number): string => `Mark as sent, ${String(n)} to Waiting on IT`,
    marking: 'Saving…',
    sentLine: (when: string, by: string, open: number): string =>
      `Sent ${when} by ${by}. ${open === 1 ? '1 item is' : `${String(open)} items are`} still waiting on IT.`,

    /* ---- a checklist's email (onboarding / offboarding, spec row 7) ---- */
    draftItEmail: 'Draft IT email',
    checklistAllWithIt:
      'Every item on this checklist is already in an email to IT. Copy the text if you need it again.',
    offboardedEmail: (name: string): string => `${name} is deactivated. Here is the email for IT.`,

    /* ---- the Queue: lanes and the bulk bar ---- */
    draftForLane: (n: number): string => `Draft email to IT for ${String(n)}`,
    bulkDraft: (n: number): string => `Draft email to IT ${String(n)}`,
    inDraft: ', in a draft email',

    /* ---- the Waiting on IT lane ---- */
    lane: 'Waiting on IT',
    laneHelp: (days: number): string =>
      `Sent to IT by email. Outside systems are not connected, so mark each item granted or removed when IT replies. Overdue ${accessPlural(days, 'day', 'days')} after the email.`,
    laneEmpty:
      'Nothing is waiting on IT. Select Needs access or Remove access items, then Draft email to IT.',
    laneOverdue: (n: number): string => `${String(n)} overdue`,
    draftsNotSent: (n: number): string => `${accessPlural(n, 'draft', 'drafts')} not sent`,
    draftLabel: 'Draft',
    draftRow: (n: number, when: string, by: string): string =>
      `Email to IT with ${accessPlural(n, 'item', 'items')}, saved ${when} by ${by}. Not sent yet, so the items are still in their lanes.`,
    openDraft: 'Open the draft',
    discard: 'Discard',
    blockTitle: 'Email to IT',
    blockSent: (when: string, by: string): string => `, sent ${when} by ${by}`,
    blockOpen: (open: number, total: number, setUp: number, remove: number): string =>
      `${String(open)} open of ${String(total)}${
        setUp > 0 && remove > 0
          ? `: ${String(setUp)} to set up, ${String(remove)} to remove`
          : setUp > 0
            ? ', all to set up'
            : ', all to remove'
      }.`,
    selectBlock: 'Select the items in this email',
    openEmail: 'Open the email',
    confirmAll: (n: number): string => `IT confirmed all ${String(n)}`,
    takeBack: 'Take back',

    /* ---- outcomes ---- */
    savedDraft: 'Draft saved. The items stay in their lanes until you mark it sent.',
    markedSent: (n: number): string =>
      `Marked sent. ${n === 1 ? '1 item is' : `${String(n)} items are`} waiting on IT.`,
    confirmedAll: (n: number): string => `IT confirmed ${accessPlural(n, 'item', 'items')}.`,
    takenBack: 'Taken back. The open items are back in their lanes.',
    discarded: 'Draft discarded. The items are back in their lanes.',
    itemGranted: 'Marked granted.',
    itemRemoved: 'Marked removed.',

    /* ---- the IT emails page ---- */
    state: {
      draft: 'Draft, not sent',
      sent: 'Waiting on IT',
      completed: 'IT finished',
      taken_back: 'Taken back',
    },
    pageHead: (waiting: number, items: number, drafts: number, done: number): string =>
      `${accessPlural(waiting, 'email', 'emails')} waiting (${accessPlural(items, 'item', 'items')}), ${String(drafts)} draft, ${String(done)} done`,
    newEmailOpen: (n: number): string => (n > 0 ? `New email to IT (${String(n)} open)` : 'New email to IT'),
    listSent: (date: string): string => `Sent ${date}`,
    listSaved: (date: string): string => `Saved ${date}`,
    listItems: (n: number): string => accessPlural(n, 'item', 'items'),
    listOpen: (n: number): string => `${String(n)} open`,
    overdue: 'overdue',
    emptyTitle: 'No emails to IT yet',
    emptyBody:
      'Outside systems are not connected to Accounts, so IT sets up and removes those accounts by hand. Pick the open work for one email, copy it into your mail app, and mark it sent; the items wait here until IT replies.',
    detailSent: (when: string, by: string, ago: string): string => `Sent ${when} by ${by}, ${ago}`,
    detailSaved: (when: string, by: string): string => `Saved ${when} by ${by}`,
    detailTakenBack: (when: string, by: string): string => `Taken back ${when} by ${by}`,
    detailOverdue: (days: number): string =>
      `Overdue: IT has had it more than ${accessPlural(days, 'day', 'days')}.`,
    openAndSend: 'Open and send',
    copyAgain: 'Copy text again',
    showText: 'Show the text',
    asksFor: 'What it asks for',
    asksCount: (n: number, open: number | null): string =>
      `${accessPlural(n, 'item', 'items')}${open === null ? '' : `, ${String(open)} still open`}`,
    theEmail: 'The email',
    asSent: 'as it was sent',
    asSaved: 'as it was saved',
    itemStatus: {
      open: 'Open',
      granted: 'Granted',
      removed: 'Removed',
      taken_back: 'Taken back',
    },
    emailsLabel: 'Emails to IT',
  },

  /* --------------------------------------------------------- other requests -- */

  /** Spec row 13: "Something else" — a request for something not in the catalog. */
  otherRequests: {
    /* ---- Request access: the Something else step ---- */
    tile: 'Something else',
    tileSub: 'Not in the list? Describe it.',
    link: 'Not in the list? Ask for something else.',
    stepTitle: 'Something else',
    stepSub: 'not in the list',
    stepLead: 'Describe what you need. An administrator decides, and adds it to the catalog if they approve.',
    whatLabel: 'What do you need?',
    whatPlaceholder: 'The name of the system or tool',
    whereLabel: 'Where does it belong? (optional)',
    whereUnknown: 'I don’t know',
    whyLabel: 'Why do you need it?',
    whyPlaceholder: 'A sentence for the administrator who decides',
    whyNeeded: 'Say why you need it, so the administrator can decide.',
    stepNote:
      'This goes to an administrator. If they approve it, they add it to the catalog and give it to you. You get an email and a notification with the answer either way.',
    pickedOther: (what: string): string => `something else: “${what}”`,

    /* ---- the admin's Queue ---- */
    laneLabel: (what: string): string => `Other: ${what}`,
    under: (place: string): string => `Under ${place}`,
    notInCatalog: 'Not in the catalog',
    noNote: 'no note',
    oneByOne: 'Other requests are approved one by one; they are not part of Approve all or the bulk bar.',

    /* ---- approve ---- */
    approveLead: 'Pre-filled from the request. Change anything before you save.',
    fromRequest: (
      person: string,
      when: string,
      what: string,
      why: string,
      where: string | null,
      first: string,
    ): string =>
      `From ${person}’s request, ${when}: “${what}”${why === '' ? '' : `. Reason: “${why}”`}${where === null ? '' : `. They filed it under ${where}`}. Saving adds this row to the catalog and gives it to ${first} in one step.`,
    giveTo: (person: string): string => `Give it to ${person}`,
    suiteGrant: (first: string, app: string): string =>
      `Suite app: ${first} gets a grant to ${app} and it appears on their launcher. No password step.`,
    loginLabel: 'Login (optional)',
    loginPlaceholder: 'email or username, never a password',
    passwordPick: 'Pick one. Managed: saved for them in the VM, never seen. Given raw: they are told it.',
    statusLabel: 'Status',
    statusNeeds: 'Needs access, IT sets it up',
    statusActive: 'Active, they already have it',
    addAndGrant: (first: string): string => `Add row and grant to ${first}`,
    approving: 'Saving…',
    approved: (row: string, person: string): string =>
      `Added “${row}” to the catalog and gave it to ${person}. They get an email.`,

    /* ---- decline ---- */
    declineTitle: (what: string): string => `Decline “${what}”`,
    declineLead: (person: string): string => `${person} gets an email and a notification with the answer.`,
    reasonLabel: 'Reason (optional)',
    reasonPlaceholder: 'For example: we already have this through another team.',
    reasonHint: 'If you write one, it is in the email and the audit log. If not, the email says no reason was given.',
    decline: 'Decline',
    declined: (person: string, withReason: boolean): string =>
      `Declined. ${person} gets an email${withReason ? ' with your reason' : ''}.`,

    /* ---- the Queue's Other requests lane (administrators only) ---- */
    laneTitle: 'Other requests',
    laneHelp: 'Not in the catalog. Approving adds a catalog row and gives it to the person in one step.',
    laneEmpty: 'No Other requests are waiting.',
    laneOverdue: (n: number): string => `${String(n)} overdue`,
    whyLine: (why: string): string => `“${why}”`,
    doneApproved: (row: string): string => `Approved, added as ${row}`,
    doneDeclined: 'Declined',

    /* ---- the person's own list on the launcher ---- */
    kind: 'not in the catalog',
    askedAs: (what: string): string => `you asked for it as “${what}”`,
    linePending: 'Waiting for an administrator. If they approve it, they add it to the catalog and give it to you.',
    lineApproved: (row: string): string => `Approved and added to the catalog as ${row}.`,
    lineDeclined: (by: string, reason: string | null): string =>
      reason === null
        ? `Declined by ${by}. No reason was given.`
        : `Reason from ${by}: “${reason}”`,
    lineCancelled: 'Cancelled.',
  },

  /* ---------------------------------------------------------- answer emails -- */

  /** Spec row 14 and the board's mail decision (19 Oct): the email every answer sends. */
  answerEmails: {
    history: 'History',
    hideHistory: 'Hide history',
    youAsked: 'You asked',
    answered: 'Answered',
    emailed: 'We emailed you the answer',
    showEmail: 'Show the email',
    hideEmail: 'Hide the email',
    none: 'No email yet.',
    to: (address: string): string => `To ${address}`,
    status: {
      pending: 'Waiting to send',
      captured: 'Captured',
      sent: 'Sent',
      failed: 'Could not send',
    },
    kind: {
      request_approved: 'Request approved',
      request_declined: 'Request declined',
      other_approved: 'Other request approved',
      other_declined: 'Other request declined',
    },

    /* ---- the admin Emails page ---- */
    captureBanner: 'Capture mode: no email is sent.',
    captureMore: 'Accounts keeps each answer here instead of sending it.',
    columns: { to: 'To', subject: 'Subject', kind: 'Kind', status: 'Status', at: 'When' },
    openRequest: 'Open the request',
    empty: 'No emails yet.',
    newer: 'Newer',
    older: 'Older',
    range: (from: number, to: number, total: number): string =>
      `${String(from)}–${String(to)} of ${String(total)}`,
  },

  /*
   * The access module's sections (SIR-7586, FINAL demo R3-2). Built in parallel and merged
   * here unchanged, so this file stays the one home for every string the app renders.
   */
  access: {
    overview: {
      heading: 'Access',
      summary: (waiting: number, overdue: number): string =>
        waiting === 0
          ? 'Nothing is waiting.'
          : `${accessPlural(waiting, 'item', 'items')} waiting, ${String(overdue)} overdue. Open a total to work through it.`,
      tile: {
        request: ACCESS_TYPE_NOUNS.request.many,
        needs_access: ACCESS_TYPE_NOUNS.needs_access.many,
        requested: ACCESS_TYPE_NOUNS.requested.many,
        remove_access: ACCESS_TYPE_NOUNS.remove_access.many,
        deactivations: 'App deactivations',
        it: 'Waiting on IT',
        overdue: 'Overdue',
      },
      tileOverdue: (n: number): string => `${String(n)} overdue`,
      tileOldest: (age: string): string => `oldest ${age}`,
      itCountsFailed:
        'Could not load the work that is with IT, so some totals show a dash. Try again in a moment.',
      /** The tile is a link; its accessible name says where it goes, not just the number. */
      tileOpen: (label: string, n: number | string): string =>
        `${label}: ${String(n)}. Open the Queue on this lane.`,
      waitingLongest: 'Waiting longest',
      openQueue: 'Open the Queue',
      openWorkBy: (level: string): string => `Open setup and removal work by ${level.toLowerCase()}`,
      openWorkByFallback: 'Open setup and removal work',
      openMatrix: 'Open the Matrix',
      noOpenWork: 'No open setup or removal work.',
      recentChanges: 'Recent access changes',
      noRecentChanges: 'No access changes yet.',
      nothingWaiting: 'Nothing is waiting.',
      /** One line under a waiting item: what kind of work, then the catalog row. */
      itemLine: (type: string, item: string): string => `${type}, ${item}`,
      hrNote:
        'HR view: requests for rows marked HR can grant directly, and setup and removal work in outside systems.',
    },
    queue: {
      hrNote:
        'HR view: requests for rows marked HR can grant directly, and setup and removal work in outside systems.',
      /** Lane titles and the type filter share these. */
      type: {
        request: ACCESS_TYPE_NOUNS.request.many,
        needs_access: ACCESS_TYPE_NOUNS.needs_access.many,
        requested: ACCESS_TYPE_NOUNS.requested.many,
        remove_access: ACCESS_TYPE_NOUNS.remove_access.many,
      },
      /** Lower-case, for the overdue strip's breakdown ("2 requests, 1 needs access"). */
      typeCount: (type: keyof typeof ACCESS_TYPE_NOUNS, n: number): string =>
        accessPlural(n, ACCESS_TYPE_NOUNS[type].one, ACCESS_TYPE_NOUNS[type].many.toLowerCase()),
      laneHelp: {
        request:
          'Approving grants a suite app at once; an outside system asks Managed or Given raw first, then becomes Needs access.',
        needs_access: 'Outside systems: IT sets these up. Mark them granted when done.',
        requested: 'Outside systems someone has asked for. Mark them granted when they are set up.',
        remove_access: 'Outside systems: IT removes or rotates these. Mark them removed when done.',
      },
      laneEmpty: 'Nothing here.',
      laneOverdue: (n: number): string => `${String(n)} overdue`,
      expand: 'Show',
      collapse: 'Hide',
      /* ---- counts and the overdue strip ---- */
      overdueCount: (n: number): string => `${String(n)} overdue`,
      oldest: (name: string, item: string, age: string): string => `Oldest: ${name}, ${item}, ${age}`,
      nothingOverdue: 'Nothing is overdue.',
      selectAllOverdue: 'Select all overdue',
      showOnlyOverdue: 'Show only overdue',
      showEverything: 'Show everything',
      /* ---- filters ---- */
      searchPlaceholder: 'Search person, login or app',
      searchLabel: 'Search the Queue by person, login or application',
      ageLabel: 'Age',
      ageAny: 'Any age',
      ageOverdue: 'Overdue only',
      ageOnTime: 'Not overdue',
      typeLabel: 'Type',
      typeAny: 'Every type',
      levelLabel: (level: string): string => `Filter by ${level.toLowerCase()}`,
      levelAny: (level: string): string => `Every ${level.toLowerCase()}`,
      personLabel: 'Person',
      personAny: 'Everyone',
      clearFilters: 'Clear filters',
      /* ---- the bulk bar ---- */
      selectedCount: (n: number): string => (n === 0 ? 'Select' : `${String(n)} selected`),
      selectAllShown: 'all shown:',
      quickOverdue: 'Overdue',
      bulkApprove: (n: number): string => `Approve ${String(n)}`,
      bulkDeny: (n: number): string => `Deny ${String(n)}`,
      bulkGranted: (n: number): string => `Mark granted ${String(n)}`,
      bulkRemoved: (n: number): string => `Mark removed ${String(n)}`,
      clearSelection: 'Clear',
      bulkFits: 'Each action applies only to the rows it fits.',
      selectRow: (name: string): string => `Select ${name}`,
      selectGroup: 'Select this shared login',
      /* ---- rows ---- */
      approve: 'Approve',
      deny: 'Deny',
      markGranted: 'Mark granted',
      markRemoved: 'Mark removed',
      approveAll: (n: number): string => `Approve all ${String(n)}`,
      approveAllArmed: (n: number): string => `Click again to approve ${String(n)}`,
      approveAllConfirm: 'Click “Approve all” again to approve every request in this lane.',
      markAllGranted: (n: number): string => `Mark all ${String(n)} granted`,
      markAllRemoved: (n: number): string => `Mark all ${String(n)} removed`,
      oneByOne: 'One by one',
      overdueAge: (age: string): string => `${age}, overdue`,
      sourceOnboarding: 'Onboarding',
      sourceSelf: 'Asked by them',
      suiteApp: 'Suite app',
      outsideSystem: 'Outside system',
      /** A request row's detail: "Onboarding, " leads an onboarding row, then the note. */
      onboardingPrefix: 'Onboarding, ',
      noteQuoted: (note: string): string => `“${note}”`,
      noNote: 'No note',
      noNoteAfterOnboarding: 'no note',
      sharedWith: (names: string): string => `Shared with ${names}`,
      sharedGroup: (item: string): string => `${item}, shared login`,
      sharedGroupWaiting: (waiting: number, holders: number): string =>
        `: ${String(waiting)} of ${String(holders)} users waiting`,
      deactivations: {
        title: 'App deactivations',
        help: (hours: number | null): string =>
          hours === null
            ? 'Waiting for each application to confirm. These rows are read-only in Accounts.'
            : `Waiting for each application to confirm. Overdue after ${String(hours)} hours. These rows are read-only in Accounts.`,
        pending: 'Pending',
        overdue: 'Overdue',
        doorbell: (attempts: number): string =>
          attempts === 1 ? '1 notification attempt' : `${String(attempts)} notification attempts`,
        overdueCount: (n: number): string =>
          accessPlural(n, 'app deactivation', 'app deactivations'),
        oldest: (name: string, app: string, age: string): string =>
          `Oldest: ${name}, ${app}, ${age}`,
      },
      /* ---- done lines (rows update in place) ---- */
      doneApprovedGranted: 'Approved, granted',
      doneApprovedNeeds: (mode: string): string => `Approved, now Needs access (${mode})`,
      doneDenied: 'Denied',
      doneGranted: 'Marked granted',
      doneRemoved: 'Marked removed',
      doneLine: (name: string, item: string, when: string): string => `${name}, ${item}, ${when}`,
      clearDone: (n: number): string => `Clear ${String(n)} done`,
      /* ---- the approve dialog (Managed / Given raw) ---- */
      approveTitleOne: (item: string, name: string): string => `Approve ${item} for ${name}`,
      approveTitleMany: (n: number): string => `Approve ${String(n)} requests`,
      approveLead:
        'Outside systems are not connected to Accounts. Say how each person will use the password before you approve. It decides what offboarding later asks IT to do.',
      managedCard:
        'Saved for them in the VM; they never see it. Offboarding removes their VM access.',
      givenRawCard: 'They are told the password. On a shared login, offboarding rotates it.',
      setAll: (n: number): string => `Set all ${String(n)}:`,
      passwordFor: (name: string): string => `Password for ${name}`,
      suiteAlongside: (n: number, apps: string): string =>
        `${accessPlural(n, 'suite app request', 'suite app requests')} (${apps}) approved with them: no password step, the app is granted at once.`,
      pickMore: (n: number): string => `Pick ${String(n)} more to approve`,
      approveGo: 'Approve',
      approveGoMany: (n: number): string => `Approve ${String(n)}`,
      approving: 'Approving…',
      /* ---- the deny dialog ---- */
      denyTitleOne: (item: string, name: string): string => `Deny ${item} for ${name}`,
      denyTitleMany: (n: number): string => `Deny ${String(n)} requests`,
      denyLead: 'Say why. The reason is kept with the request and shown to the person.',
      denyNoteLabel: 'Reason',
      denyNotePlaceholder: 'For example: use the shared support login instead',
      denyNoteRequired: 'A reason is required to deny.',
      denyGo: 'Deny',
      denyGoMany: (n: number): string => `Deny ${String(n)}`,
      denying: 'Denying…',
      /* ---- toasts ---- */
      toastApproved: (n: number): string =>
        n === 1 ? 'Request approved.' : `Approved ${String(n)} requests, one audit row each.`,
      toastDenied: (n: number): string =>
        n === 1 ? 'Request denied.' : `Denied ${String(n)} requests, one audit row each.`,
      toastGranted: (n: number): string =>
        n === 1 ? 'Marked granted.' : `Marked ${String(n)} granted, one audit row each.`,
      toastRemoved: (n: number): string =>
        n === 1 ? 'Marked removed.' : `Marked ${String(n)} removed, one audit row each.`,
      /* ---- states ---- */
      empty: 'Nothing is waiting.',
      emptyFiltered: 'Nothing matches these filters.',
      loadFailed: 'The Queue could not be loaded.',
    },
    catalog: {
      /** Between two "Filed under" steps; decorative, the steps are labelled themselves. */
      pickerStep: '→',
      /** The name preview while the Row name box is still empty. */
      namePending: '…',
      intro: (levels: string): string =>
        `Filed as ${levels}. Open a section to change it; switches save at once.`,
      levelArrow: ' → ',
      expandAll: 'Expand all',
      collapseAll: 'Collapse all',
      expand: 'Expand',
      collapse: 'Collapse',
      loadFailedTitle: 'The catalog could not be loaded',
      loadFailedBody: 'Something went wrong reading the catalog. Try again in a moment.',

      /* ---- catalog rows ---- */
      rowsTitle: 'Catalog rows',
      rowsAside: (hidden: number, hr: number, apps: number): string =>
        `${String(hidden)} hidden, ${String(hr)} HR can grant, ${accessPlural(apps, 'suite app', 'suite apps')}`,
      searchPlaceholder: 'Search catalog',
      searchLabel: 'Search the catalog',
      showRetired: 'Show retired',
      rowCount: (n: number): string => accessPlural(n, 'row', 'rows'),
      addRow: 'Add catalog row',
      colRow: 'Row',
      colPlace: 'Filed under',
      colApp: 'Connected app',
      colRequestable: 'Requestable',
      colHrCanGrant: 'HR can grant',
      colPeople: 'People',
      none: 'None',
      groupSuiteApps: 'Suite applications',
      noRows: 'No catalog rows yet. Add the first one to start the catalog.',
      noRowMatch: 'No row matches.',
      retiredOn: (date: string): string => `Retired ${date}`,
      toggleRequestable: (name: string): string => `Requestable: ${name}`,
      toggleHrCanGrant: (name: string): string => `HR can grant directly: ${name}`,
      savedRequestableOn: (name: string): string => `${name} can now be requested.`,
      savedRequestableOff: (name: string): string => `${name} is now hidden from requests.`,
      savedHrOn: (name: string): string => `HR can now give ${name} directly.`,
      savedHrOff: (name: string): string => `${name} now needs an administrator's approval.`,

      /* ---- hierarchy (levels) ---- */
      hierarchyTitle: 'Hierarchy',
      hierarchyLead:
        'Catalog rows are filed under these levels, top first. Name them the way your team talks about access: companies, or groups such as Global and Office Tools. A row can stop at any level; each row’s Filed under setting picks its place.',
      levelNumber: (depth: number): string => `Level ${String(depth)}`,
      levelNameLabel: (depth: number): string => `Name of level ${String(depth)}`,
      levelUsage: (entries: number, rows: number): string =>
        `${accessPlural(entries, 'entry', 'entries')}, ${accessPlural(rows, 'row stops', 'rows stop')} here`,
      save: 'Save',
      addLevelLabel: 'Name of a new level',
      addLevelPlaceholder: 'System',
      addLevel: 'Add level',
      addLevelHint:
        'A new level sits below the deepest one, for example the system or the account type. Levels are only added and renamed; nothing is deleted.',
      levelRenamed: (name: string): string =>
        `Level renamed. Every list and filter now says “${name}”.`,
      levelAdded: (name: string): string => `Level “${name}” added. Add its entries, then file rows under them.`,
      noLevels: 'No levels yet. Add the first level to start filing the catalog.',
      filedNowTitle: 'How the catalog is filed now',
      filedOnNode: 'On it:',
      notFiledGroup: 'Not filed',
      suiteAppsNotFiled: 'Suite applications that are not filed are listed under Suite applications: their access is a grant.',

      /* ---- one section per level (nodes) ---- */
      levelAside: (depth: number, retired: number): string =>
        `level ${String(depth)}${retired > 0 ? `, ${String(retired)} retired` : ''}`,
      nodeNameLabel: (name: string): string => `Name of ${name}`,
      nodeUnder: (parent: string): string => `under ${parent}`,
      nodeTop: 'top level',
      nodeRows: (n: number): string => accessPlural(n, 'row', 'rows'),
      move: 'Move',
      retire: 'Retire',
      retiredNode: (date: string): string => `retired ${date}`,
      noNodes: (levelPlural: string): string =>
        `No ${levelPlural.toLowerCase()} yet. Add the first one below.`,
      newNodeLabel: (level: string): string => `New ${level.toLowerCase()}`,
      newNodePlaceholder: 'Northwind Home',
      parentLabel: 'Under',
      parentTop: 'Nothing (top level)',
      addNode: (level: string): string => `Add ${level.toLowerCase()}`,
      nodeHint:
        'Renaming changes every catalog row filed under it; the old name stays in the audit log. Retired entries are kept, never deleted.',
      nodeAdded: (name: string): string => `${name} added.`,
      nodeRenamed: (name: string): string => `Renamed to ${name}.`,
      nodeMoved: (name: string): string => `${name} moved.`,
      nodeRetired: (name: string): string => `${name} retired. Its history is kept.`,
      moveTitle: (name: string): string => `Move ${name}`,
      moveLead:
        'Pick what it sits under. Every catalog row filed under it moves with it; the move is recorded in the audit log.',
      moveConfirm: 'Move',
      retireNodeTitle: (name: string): string => `Retire ${name}?`,
      retireNodeBody:
        'It stops appearing in pickers and filters. Nothing is deleted: rows filed under it keep their history, and the audit log keeps every change.',
      retireConfirm: 'Retire',

      /* ---- row drawer ---- */
      drawerClose: 'Close row',
      connectedTo: (app: string): string => `connected to ${app}`,
      outsideSystem: 'outside system',
      chipRequestable: 'Requestable',
      chipHidden: 'Hidden from requests',
      summary: (people: number, logins: number | null): string =>
        `${accessPlural(people, 'person', 'people')} with access${
          logins === null ? ', access lives in Grants' : `, ${accessPlural(logins, 'distinct login', 'distinct logins')}${logins < people ? ' (some shared)' : ''}`
        }.`,
      retireRow: 'Retire row',
      settingsTitle: 'Row settings',
      settingsAside: (requestable: boolean, app: string | null, hr: boolean): string =>
        `${requestable ? 'Requestable' : 'Hidden'}, ${app === null ? 'outside system' : `connected to ${app}`}, ${hr ? 'HR can grant' : 'administrators approve'}`,
      peopleTitle: 'People with access',
      historyTitle: 'Row history',
      nobodyHasIt: 'Nobody has this yet.',
      addPerson: 'Add a person',
      grantedOn: (date: string): string => `Granted ${date}`,
      readOnlyRetired: 'This row is retired. Its settings are shown as they were; nothing here can be changed.',
      readOnlyViewer: 'View only. Only administrators change the Catalog.',
      rowSaved: (name: string): string => `Saved. The row is now “${name}”.`,
      retireRowTitle: (name: string): string => `Retire ${name}?`,
      retireRowBody:
        'Nobody can request it any more and it leaves the pickers. Nothing is deleted: everyone’s records and grants stay, with their history.',
      rowRetired: (name: string): string => `${name} retired. Its history is kept.`,

      /* ---- row fields (drawer and add dialog) ---- */
      filedUnderLabel: 'Filed under',
      filedUnderHint:
        'Pick where it belongs, then stop or go one level deeper. A row can stop at any level.',
      filedUnderNow: (path: string): string => `Now: ${path}.`,
      pickerNone: 'None (not filed)',
      pickerStop: 'Stop here',
      pickerLevel: (depth: number): string => `Step ${String(depth)}`,
      rowNameLabel: 'Row name',
      rowNamePlaceholder: 'Seller Central',
      rowNameHint: 'Shown after the place, so two rows filed in one place can be told apart.',
      willBeCalled: (name: string): string => `Will be called ${name}`,
      appLabel: 'Connected app',
      appHint:
        'A registered application means access is a grant: approving a request grants the app. “None” means an outside system: access is a record here (login, case login, status, notes) and IT does the work.',
      requestableTitle: 'Requestable',
      requestableNote:
        'When off, the row is hidden: nobody can ask for it on the launcher. Admins and HR can still add it to a person.',
      approverLabel: 'Approver',
      approverAdmins: 'Administrators',
      approverHint:
        'Who approves requests for this row. Administrators for now; the setting is ready for a person or a role later.',
      hrTitle: 'HR can grant directly',
      hrNote:
        'When on, HR can give this access without a request, and can approve requests for it. When off, HR’s picks become requests for an administrator.',

      /* ---- add dialog ---- */
      addTitle: 'Add catalog row',
      addLead:
        'File it under any level of the hierarchy, or connect it to a suite app. Requests for it are approved by administrators.',
      addConfirm: 'Add row',
      rowAdded: (name: string): string => `Catalog row added: ${name}.`,
      nameRequired: 'Give the row a name.',
    },
    matrix: {
      loadFailedTitle: 'The matrix could not be loaded',
      loadFailedBody: 'Something went wrong reading who has what. Try again in a moment.',
      viewLabel: 'Matrix view',
      viewRows: 'Row view',
      viewTable: 'Table view',
      viewHeat: 'Heatmap',
      every: (level: string): string => `Every ${level.toLowerCase()}`,
      filterLabel: (level: string): string => `Filter by ${level.toLowerCase()}`,
      peopleLabel: 'People',
      peopleActive: 'Active people',
      peopleAll: 'Everyone',
      searchPlaceholder: 'Find a person',
      searchLabel: 'Find a person',
      showLabel: 'Show',
      show: {
        all: 'Show every login',
        attention: 'Only what needs work',
      },
      exportWorkbook: 'Export workbook',
      exportingWorkbook: 'Exporting workbook…',
      exportFailed: 'The workbook could not be exported. Try again.',
      exportComplete: 'Access workbook downloaded.',
      colPerson: 'Person',
      colRow: 'Catalog row',
      deactivated: 'deactivated',
      notFiled: 'Not filed',
      nobodyMatches: 'Nobody matches.',
      noRows: 'No catalog rows match.',
      /** The Suite apps column group (row view) and row group (table view), FINAL demo. */
      suiteApps: 'Suite apps',
      appCell: {
        granted: 'Granted',
        requested: 'Asked',
        revoked: '·',
        none: '·',
      },
      appCellTitle: (person: string, app: string, status: string): string =>
        `${person}, ${app}: ${status}.`,
      appStatusWord: {
        granted: 'granted',
        requested: 'requested',
        revoked: 'revoked',
        none: 'not granted',
      },
      counts: (people: number, apps: number, rows: number, groups: number): string =>
        `${accessPlural(people, 'person', 'people')}, ${accessPlural(apps, 'suite app', 'suite apps')} and ${accessPlural(rows, 'catalog row', 'catalog rows')} under ${accessPlural(groups, 'group', 'groups')}`,
      legendLogin: 'login',
      legendActive: 'active',
      legendNeeds: 'IT has to set it up',
      legendRequested: 'requested',
      legendRemove: 'IT has to take it away',
      legendShared: 'Underlined login: shared with others',
      legendClick: 'Click any cell for the record and its history; an empty cell adds access.',
      cellEmpty: '·',
      cellTitle: (
        person: string,
        row: string,
        status: string,
        login: string,
        shared: number,
        mode: string,
      ): string =>
        `${person}, ${row}: ${status}, ${login}${shared > 0 ? `, shared with ${accessPlural(shared, 'other person', 'other people')}` : ''}, password ${mode.toLowerCase()}. Click for its history.`,
      cellEmptyTitle: (person: string, row: string): string =>
        `${person}, ${row}: no access. Click to add it.`,
      /* ---- empty cell (FINAL demo): a small dialog before the add form ---- */
      emptyLead: 'No access recorded.',
      emptyBody: (person: string, row: string): string => `Add a record for ${person} on ${row}.`,
      emptyDeactivated: (person: string): string => `${person} is deactivated.`,
      emptyAdd: 'Add access…',
      /* ---- heat grid (round-2 demo A3): squares and a side list ---- */
      heatLabel: 'Heatmap',
      heatPick: 'Pick a square',
      heatPickBody: (top: string, next: string): string =>
        `Each square is one ${top} on one ${next}. The number is how many people have it; darker means more. A yellow dot means work is open there.`,
      heatTopFallback: 'group',
      heatNextFallback: 'entry below it',
      heatOpenWork: (n: number): string => (n > 0 ? `Open work · ${String(n)}` : 'Open work'),
      heatShowAll: (n: number): string => `Show all ${String(n)}`,
      heatShowFewer: 'Show fewer',
      heatNothingOpen: 'Nothing open.',
      heatNoRow: 'No catalog row here.',
      heatSquareTitle: (group: string, column: string, people: number, open: number): string =>
        `${group}, ${column}: ${accessPlural(people, 'person', 'people')}${open > 0 ? `, ${String(open)} open` : ''}. Click for the list.`,
      heatAppTitle: (app: string, granted: number, asked: number): string =>
        `${app}: ${accessPlural(granted, 'person', 'people')} granted${asked > 0 ? `, ${String(asked)} asked` : ''}. Click for the list.`,
      heatSquareMeta: (rows: number, people: number): string =>
        `${accessPlural(rows, 'catalog row', 'catalog rows')} · ${accessPlural(people, 'person', 'people')}`,
      heatAppMeta: (granted: number, asked: number): string =>
        `${accessPlural(granted, 'person', 'people')} granted${asked > 0 ? ` · ${String(asked)} asked` : ''}`,
      heatClose: 'Close',
      heatNobody: 'Nobody here.',
    },
    person: {
      /* ---- header additions ---- */
      chipHr: 'HR',
      roleHr: 'HR',
      chipIt: 'IT',
      roleIt: 'IT',
      itTitle: 'IT user',
      itNote: 'For now, IT has the same Accounts access as HR.',
      hrCannotDeactivateAdmin: 'HR and IT cannot deactivate an administrator.',
      /** Shown to HR where the sessions count would link to the admin-only Sessions screen. */
      hrNoSessionsScreen: 'Sessions are managed by administrators.',

      /* ---- the Access section ---- */
      facts: {
        outside: (n: number): string => accessPlural(n, 'outside system', 'outside systems'),
        attention: (n: number): string => `${String(n)} need attention`,
        pending: (n: number): string => accessPlural(n, 'request waiting', 'requests waiting'),
      },
      openInQueue: 'Open in Queue',
      glanceTitle: 'Access at a glance',
      glanceAside: (records: number, groups: number, level: string): string =>
        `${accessPlural(records, 'outside system', 'outside systems')} under ${String(groups)} ${level.toLowerCase()}${groups === 1 ? '' : 's'}`,
      glanceSuiteApps: 'Suite apps',
      glanceNoApps: 'No suite apps are registered.',
      /** HR cannot read grants (admin-gated), so the suite-app line is not drawn for HR. */
      glanceLegend: 'Hover a pill for the login; click it for the record.',
      glanceLegendAdmin: 'Hover a pill for the login; click it for the record and its history.',
      glanceOnGroup: (level: string): string => `On the ${level.toLowerCase()}`,
      glanceGroupColumn: 'Group',
      glanceEmpty: 'No outside-system access recorded. Use Add access.',
      glanceEmptyInactive: 'Nothing left in outside systems.',
      addAccess: 'Add access',
      linesTitle: 'Line by line',
      linesAside: 'logins, notes, edit and history for each one',
      requestsTitle: 'Requests',
      requestsAsideWaiting: 'some still waiting',
      requestsAsideDecided: 'all decided',
      requestsEmpty: 'No access requests for this person.',
      expand: 'Expand',
      collapse: 'Collapse',
      showRemoved: (n: number): string => `Show removed (${String(n)})`,
      showRemovedPlain: 'Show removed',
      noLoginRecorded: 'No login recorded',
      sharedWith: (n: number): string => `shared with ${String(n)}`,
      sharedWithNames: (names: string): string => `Same login also used by ${names}`,
      caseLogin: (login: string): string => `Case login ${login}`,
      passwordLine: (mode: string): string => `Password: ${mode}`,
      markGranted: 'Mark granted',
      markRemoved: 'Mark removed',
      edit: 'Edit',
      history: 'History',
      requestFromOnboarding: 'from onboarding',
      requestAskedBy: (who: string, when: string): string => `Asked by ${who} ${when}`,
      requestDecided: (status: string, who: string, when: string): string =>
        `${status.toLowerCase()} by ${who} ${when}`,
      requestNote: (note: string): string => ` — “${note}”`,
      someone: 'someone',
      loadFailed: 'Access for this person could not be read.',

      /* ---- Users summary ---- */
      summaryButton: 'Summary of everyone',
      summaryTitle: 'Everyone',
      summaryLead: 'A summary of the directory and the access work in progress. Choose a person to see everything they have.',
      summarySearchHint: 'Search the list on the left by name, email or team.',
      totals: {
        active: 'Active',
        deactivated: 'Deactivated',
        admins: 'Administrators',
        hr: 'HR',
        it: 'IT',
        open: 'Open access work',
      },
      /** The HR count is read from the loaded list, which is capped — said so when it is. */
      hrCountCapped: 'of the people loaded',
      openWorkTitle: 'People with open access work',
      openWorkAside: 'requests, setup and removal waiting',
      openWorkEmpty: 'Nothing is waiting for anyone.',
      openWorkCount: (n: number): string => accessPlural(n, 'item', 'items'),
      /** Overdue is said in words, never by colour alone. */
      openWorkCountOverdue: (n: number): string => `${accessPlural(n, 'item', 'items')}, overdue`,
      recentTitle: 'Recent access changes',
      recentAside: 'records, catalog and requests',
      recentEmpty: 'No access changes yet.',
      recentBy: (who: string): string => `by ${who}`,

      /* ---- HR flag ---- */
      hrTitle: 'HR',
      hrNote:
        'Can create and deactivate members and manage their access in this suite. HR cannot touch administrators, applications, grants or settings.',
      createMembersOnly: 'You create members only. Administrators are made by an administrator.',
      toast: {
        hrOn: (name: string): string => `${name} is now HR.`,
        hrOff: (name: string): string => `${name} is no longer HR.`,
        itOn: (name: string): string => `${name} is now IT.`,
        itOff: (name: string): string => `${name} is no longer IT.`,
      },
    },
    onboarding: {
      leadHr: 'HR onboarding: the person, then what they start with.',
      leadAdmin: 'The person, then what they start with. Starting access is optional.',
      stepPerson: 'Person',
      stepAccess: 'Starting access',
      stepReview: 'Review',
      progress: 'Create user progress',
      back: 'Back',
      nextAccess: 'Next: starting access',
      nextReview: 'Next: review',
      create: 'Create user and start onboarding',
      creating: 'Creating…',
      jobTitle: 'Job title (optional)',
      jobTitlePlaceholder: 'Customer support',
      membersOnly: 'As HR you create members only. An administrator can promote someone later.',
      they: 'they',
      pickLead: (firstName: string, hrOnly: boolean): string =>
        `Pick what ${firstName} needs on day one. Direct rows are given at once${hrOnly ? ' when marked “HR can grant directly”' : ''}; Request rows go to an administrator. Direct outside systems become IT setup items on the checklist.`,
      search: 'Search the catalog',
      catalogError: 'The access catalog could not be loaded. Try again in a moment.',
      noMatches: 'Nothing matches.',
      outsideSystem: 'outside system',
      hiddenRow: 'hidden from employee requests',
      givenAtOnce: 'given at once',
      requestToAdmin: 'request to an administrator',
      pickSummary: (picks: number, direct: number, requests: number): string =>
        `${accessPlural(picks, 'pick', 'picks')} · ${String(direct)} direct · ${String(requests)} requests`,
      givenNow: 'Given now',
      requestsToAdmin: 'Requests to admin',
      itSetupItems: 'IT setup items',
      noPicks: 'No starting access picked. You can add access later.',
      itSetsUp: 'IT sets it up',
      suiteGrant: 'suite app — grant',
      request: 'request',
      passwordNotice: (name: string, email: string): string =>
        `Creating ${name} (${email}) shows a one-time temporary password, exactly as today. Hand it over securely; it is never stored or shown again.`,
      created: (name: string, direct: number, requests: number): string =>
        `${name} created. ${String(direct)} given now, ${String(requests)} sent for approval.`,
      checklistTitle: 'Onboarding checklist',
      checklistProgress: (done: number, total: number, when: string): string =>
        `${String(done)} of ${String(total)} done · started ${when}`,
      checklistComplete: 'Onboarding complete',
      checklistWaiting: 'Waiting for an administrator to approve',
      checklistWaitingLine: (when: string): string =>
        `Requested ${when} — becomes a setup item when approved`,
      checklistIt: 'IT setup · ticked by HR or an administrator',
      checklistEmpty: 'No IT setup is waiting.',
      checklistLoadFailed: 'The onboarding checklist could not be loaded.',
      noLogin: 'no login recorded',
      markDone: 'Mark done',
      reopen: 'Reopen',
      done: (when: string): string => `Done ${when}`,
      toastDone: (name: string): string => `${name} marked done.`,
      toastReopened: (name: string): string => `${name} reopened.`,
    },
    offboarding: {
      title: (name: string): string => `Offboard ${name}`,
      step: (step: number): string =>
        step === 1
          ? 'Step 1 of 3: review access'
          : step === 2
            ? 'Step 2 of 3: choose outside-system work'
            : 'Step 3 of 3: confirm offboarding',
      reviewWarning:
        'Offboarding ends this person’s Accounts access now. Suite applications then confirm their own deactivation; outside-system work stays on the checklist until someone marks it done.',
      suiteApps: 'Suite applications',
      noSuiteApps: 'No live suite-application grants.',
      willAskApp: 'Deactivation requested',
      outsideLogins: 'Outside-system logins',
      outsideSummary: (all: number, shared: number): string =>
        `${accessPlural(all, 'login', 'logins')} · ${accessPlural(shared, 'shared login', 'shared logins')}`,
      chooseLead:
        'Choose what IT must do for each shared login. Personal logins use the server’s required removal action.',
      noOutsideLogins: 'No outside-system access needs removal.',
      personalLogin: 'personal login',
      sharedWith: (names: string): string => `shared with ${names}`,
      passwordLabel: 'Password:',
      passwordNote: {
        managed: 'saved in the VM, they never saw it',
        given_raw: 'they were told it',
      },
      choiceFor: (name: string): string => `Offboarding action for ${name}`,
      action: {
        remove: 'Remove account',
        remove_vm_access: 'Remove VM access',
        rotate_shared_password: 'Rotate shared password',
        remove_from_shared_mailbox: 'Remove from shared mailbox',
      },
      alreadyOpen:
        'An offboarding checklist is already open for this person. Finish that checklist instead of starting another.',
      finalWarning: (name: string): string =>
        `This deactivates ${name} immediately. The remaining removal work is tracked until it is complete.`,
      signInEnds: 'Their Accounts sessions end and they can no longer sign in.',
      appCount: (n: number): string =>
        `${accessPlural(n, 'suite application', 'suite applications')} asked to deactivate access`,
      actionCount: (n: number, action: string): string => `${action}: ${String(n)}`,
      removeAccountTitle: (logins: number): string =>
        `${accessPlural(logins, 'shared login', 'shared logins')} set to Remove account.`,
      removeAccountBody: (people: number): string =>
        `That locks out ${accessPlural(people, 'other person', 'other people')}. Go back if that is not what you mean.`,
      confirm: 'Deactivate and start checklist',
      offboarding: 'Offboarding…',
      completed: (name: string): string => `${name} deactivated. Offboarding checklist started.`,
    },
    offboardingChecklist: {
      title: 'Offboarding checklist',
      progress: (done: number, total: number, when: string): string =>
        `${String(done)} of ${String(total)} done · started ${when}`,
      complete: 'Offboarding complete',
      outsideSystems: 'Outside systems · ticked by HR or an administrator',
      appDeactivations: 'Suite applications · confirmed by each application',
      markDone: 'Mark done',
      reopen: 'Reopen',
      doneAt: (when: string): string => `Done ${when}`,
      waitingForApp: 'Waiting for the application to confirm deactivation.',
      confirmedAt: (when: string): string => `Confirmed ${when}`,
      state: { pending: 'Pending', overdue: 'Overdue', confirmed: 'Confirmed' },
      appReadOnly:
        'Application items are read-only here. They complete only when that application confirms the deactivation.',
      unknownApp: 'Unknown application',
      empty: 'No removal work is waiting.',
      loadFailed: 'The offboarding checklist could not be loaded.',
      done: (name: string): string => `${name} marked done.`,
      reopened: (name: string): string => `${name} reopened.`,
    },
    record: {
      /* ---- record dialog ---- */
      viewTitle: (item: string, person: string): string => `${item} — ${person}`,
      editTitle: (item: string): string => `Edit access — ${item}`,
      editLead: 'Every changed field is recorded with its old and new value.',
      loginLabel: 'Login',
      loginPlaceholder: 'email or username, never a password',
      caseLoginLabel: 'Case login',
      caseLoginPlaceholder: 'the login used to open seller cases, if different',
      statusLabel: 'Status',
      passwordLabel: 'Password',
      passwordHint:
        'Never the password itself. This only says whether the person knows it, which decides what offboarding asks IT to do.',
      passwordPick: 'Pick how the password is handled…',
      notesLabel: 'Notes',
      optional: '(optional)',
      addedLine: (who: string, when: string): string => `Added by ${who} on ${when}`,
      removedLine: (who: string, when: string): string => `Removed by ${who} on ${when}`,
      sharedCallout: (names: string): string => `Shared login, also used by ${names}.`,
      sharedEditCallout: (login: string, names: string, person: string): string =>
        `Shared login. ${login} is also used here by ${names}. Changing it here changes only ${person}’s record.`,
      historyTitle: 'History',
      historyAdminsOnly: 'History is the audit log, which only administrators can read.',
      markGranted: 'Mark granted',
      markRemoved: 'Mark removed',
      edit: 'Edit',
      retire: 'Retire, entered by mistake',
      retireTitle: 'Retire this record?',
      retireBody:
        'Use this only for a record entered by mistake. It is kept with its history, and it leaves every list. To record that IT took the access away, use Mark removed instead.',
      retireSubmit: 'Retire record',
      save: 'Save',
      unchanged: 'Nothing changed.',
      saved: (n: number): string => `Saved ${accessPlural(n, 'change', 'changes')}. History updated.`,
      granted: (item: string): string => `${item} marked granted.`,
      removed: (item: string): string => `${item} marked removed.`,
      retired: (item: string): string => `${item} retired.`,

      /* ---- add record ---- */
      addTitle: (person: string): string => `Add access for ${person}`,
      addTitleNoPerson: 'Add access',
      addLead:
        'Outside systems become records here. Suite apps are given from Application access, as today.',
      /** The Matrix's add form, whose What picker offers suite apps too (FINAL demo). */
      addLeadWithApps:
        'Suite apps become grants; outside systems become records here. Rows hidden from requests can still be added by you.',
      personLabel: 'Person',
      personPick: 'Pick a person…',
      itemLabel: 'What',
      itemPick: 'Pick a catalog row…',
      itemNone: 'There is no outside-system row this person can be given.',
      itemNoneAny: 'There is no catalog row this person can be given.',
      suiteAppOption: (name: string): string => `${name} (suite app)`,
      hrNotDirect:
        'This row is not marked “HR can grant directly”, so an administrator has to approve it. Sending asks them.',
      sameLogin: (names: string): string => `This login is already used here by ${names}. It will show as shared.`,
      statusActiveLong: 'Active, they already have it',
      statusNeedsLong: 'Needs access, IT has to set it up',
      passwordRequired: 'Pick how the password is handled.',
      itemRequired: 'Pick a catalog row.',
      personRequired: 'Pick a person.',
      add: 'Add record',
      addPick: 'Add',
      grantApp: (app: string): string => `Grant ${app}`,
      added: (item: string): string => `Record added for ${item}.`,
      addedQueued: (item: string): string => `Record added for ${item}; it is in the Queue for IT.`,
      /** HR's Add access on a row it cannot grant: a request for an administrator, not a record. */
      sendRequest: 'Send request',
      requestSent: 'Request sent to an administrator.',

      /* ---- history list ---- */
      historyEmpty: 'No history yet.',
      historyFailed: 'The history could not be read.',
      historyCreated: 'Record created',
      historyChange: (field: string, from: string, to: string): string => `${field}: ${from} → ${to}`,
      historyWhen: 'When',
      historyWho: 'Who',
      historyWhat: 'What',
      historyChangeCol: 'Change',
      fieldNames: {
        login: 'Login',
        caseLogin: 'Case login',
        notes: 'Notes',
        status: 'Status',
        passwordHandling: 'Password',
      },
      system: 'System',
    },
    request: {
      /* ---- the launcher entry point ---- */
      entryTitle: 'Request access',
      entrySub: 'An app or an outside system',
      entryBody:
        'Ask for access to a suite app or a marketplace account. An administrator approves it, and you see the answer here.',
      entryBodyList: 'Ask for access to a suite app or a marketplace account. An administrator approves it.',
      entryAction: 'Request access',
      entryMark: '+',

      /* ---- the picker ---- */
      dialogTitle: 'Request access',
      leadStep1: 'Step 1 of 2: pick the group the system belongs to, or search.',
      leadStep2: 'Tick what you need here. You can go back and add from another group; your picks stay.',
      leadSearch: 'Tick what you need. Clear the search to go back to the groups.',
      searchStep1: 'Search everything, or pick a group',
      searchLabel: 'Search apps and systems',
      allGroups: 'All groups',
      /** The group for catalog rows bound to a suite application. */
      suiteGroup: 'Suite applications',
      systems: (n: number): string => (n === 1 ? '1 system' : `${String(n)} systems`),
      available: (n: number): string => `${String(n)} you can ask for`,
      picked: (n: number): string => `${String(n)} picked`,
      pickedLine: (n: number): string => (n === 1 ? '1 item picked:' : `${String(n)} items picked:`),
      nothingPicked: 'Nothing picked yet.',
      reasonLabel: 'Why do you need it? (a short reason helps the approver)',
      reasonPlaceholder: 'Covering eBay returns while a teammate is out.',
      suiteHint: 'Suite app, opens from your launcher',
      outsideHint: 'Outside system, you sign in to it directly',
      youHaveIt: 'You have it',
      requested: 'Requested',
      noMatch: 'Nothing matches.',
      emptyCatalog: 'Nothing can be requested right now. Ask an administrator if you need something.',
      send: 'Send request',
      sendMany: (n: number): string => `Send ${String(n)} requests`,
      sending: 'Sending…',
      sentOne: 'Request sent.',
      sentMany: (n: number): string => `Requests sent for ${String(n)} items.`,
      /** Some of a batch went through and some did not; the server's reason for the first. */
      sentPartly: (sent: number, failed: number, reason: string): string =>
        `${String(sent)} sent, ${String(failed)} not sent: ${reason}`,

      /* ---- My access requests ---- */
      mySection: 'My access requests',
      myEmpty: 'You have not asked for anything yet.',
      myFootnote:
        'A suite app appears under Applications once it is granted. An outside system (a marketplace account, a tool) only shows its status here: you sign in to it directly.',
      granted: 'Granted',
      linePending: 'Waiting for an administrator to approve it.',
      lineGranted: (name: string): string => `${name} is on your launcher above. Open it from there.`,
      lineApprovedApp: 'Approved; this access has since been removed.',
      lineApprovedOutside: 'Approved. IT sets it up next; you sign in to it directly.',
      lineDenied: (reason: string): string => `Reason: “${reason}”`,
      lineDeniedNoReason: 'No reason was given.',
      lineCancelled: 'You cancelled this request.',
      asked: (date: string): string => `Asked ${date}`,
      decided: (date: string): string => `Answered ${date}`,
      cancel: 'Cancel request',
      cancelling: 'Cancelling…',
      cancelled: 'Request cancelled.',
    },
    bell: {
      bell: 'Notifications',
      bellUnread: (n: number): string =>
        n === 1 ? 'Notifications, 1 unread' : `Notifications, ${String(n)} unread`,
      title: 'Notifications',
      markAllRead: 'Mark all read',
      close: 'Close notifications',
      unread: 'Unread',
      empty: 'You’re all caught up.',
      /** The badge caps here so it never outgrows the rail. */
      overflow: '99+',
      /* ---- what each notification says ---- */
      asked: (person: string, item: string): string => `${person} asked for ${item}`,
      askedBody: 'Waiting in the Queue for a decision.',
      approved: (item: string): string => `Approved: ${item}`,
      approvedBody: 'Your request was approved.',
      denied: (item: string): string => `Denied: ${item}`,
      deniedBody: (reason: string): string => `Reason: “${reason}”`,
      deniedNoReason: 'No reason was given.',
    },
  },

  /* --------------------------------------------------------------------- users -- */

  users: {
    searchPlaceholder: 'Search name, email, team',
    searchLabel: 'Search people',
    /** Fed from `meta.summary` — the whole directory, never the loaded page. */
    meta: (active: number, deactivated: number, admins: number): string =>
      `${String(active)} active · ${String(deactivated)} deactivated · ${String(admins)} administrators`,
    sortBy: 'Sort by',
    sortName: 'Name (A–Z)',
    sortCreated: 'Newest first',
    sortStatus: 'Active first',
    noneMatch: 'Nobody matches that search.',
    emptyTitle: 'No users yet',
    emptyBody: 'Create the first person and they will show up here with their access.',
    emptyAction: 'Create the first user',
    chipTemp: 'TEMP',
    chipAdmin: 'ADMIN',
    statusActive: 'Active',
    statusDeactivated: 'Deactivated',
    roleAdmin: 'Administrator',
    roleMember: 'Member',
    mustChange: 'Must change password',
    createdLabel: 'Created',
    passwordLabel: 'Password',
    passwordTemporary: 'Temporary — must change at next sign-in',
    passwordSettled: 'Set by the user',
    applicationsLabel: 'Applications',
    edit: 'Edit',
    resetPassword: 'Reset password',
    deactivate: 'Deactivate',
    reactivate: 'Reactivate',
    securityTitle: 'Security',
    twoStepTitle: 'Two-step verification',
    /**
     * The 2FA wave supersedes Ruling 3: `UserPublic` carries three status facts
     * (`twoFactorEnabled`, `twoFactorEnrolledAt`, `twoFactorResetAt`) and nothing else, so
     * exactly the demo's three states render — on, reset-pending (amber), off. Off is an
     * ORDINARY state, never a warning. Where the demo's notes claimed "they will be asked
     * to set it up at their next sign-in", that is true only under an operator requirement
     * this screen cannot see — so each note says only what the shape can back (operator-
     * confirmed for the reset state in Wave 3C).
     */
    twoStepChipOn: 'Two-step on',
    twoStepChipReset: 'Two-step re-enroll',
    twoStepChipOff: 'Two-step not set up',
    /** The demo's integration spec: the enrolment date is APPENDED to the note when known. */
    twoStepNoteEnrolled: (date: string): string =>
      `An authenticator app is enrolled. Enrolled ${date}.`,
    twoStepNoteEnabled: 'An authenticator app is enrolled.',
    /** The amber state's actionable facts: reset, when, and not set up again since. */
    twoStepNoteReset: (date: string): string =>
      `Two-step was reset ${date} and they have not set it up again. Their password alone signs them in.`,
    twoStepNoteOff: 'No authenticator app is enrolled.',
    resetTwoStep: 'Reset two-step',
    /**
     * A live count now exists — `UserPublic.activeSessionCount`, derived from the sessions
     * table (display-live rows only). Supersedes Ruling 1's "nothing may claim a count": the
     * row's own label states it, correct singular/plural, zero in words rather than a digit.
     */
    sessionsCount: (count: number): string =>
      count === 0
        ? 'No active sessions'
        : count === 1
          ? '1 active session'
          : `${String(count)} active sessions`,
    sessionsNote:
      'Ends every signed-in device at once, in every application. Their password and access stay as they are.',
    signOutEverywhere: 'Sign out of all sessions',
    accessTitle: 'Application access',
    grantApplication: 'Grant an application',
    noAccess: 'This person cannot open any application yet.',
    grantedLine: (at: string): string => `Granted ${at}`,
    selectPrompt: 'Choose somebody on the left to see their access.',
  },

  applications: {
    searchPlaceholder: 'Search name, key or address',
    searchLabel: 'Search applications',
    /**
     * Fed from `meta.summary` — every registered application, never the loaded page. An
     * application that has never reported a status counts toward none of the three health
     * numbers, so they do not have to add up to `active`.
     */
    meta: (active: number, healthy: number, maintenance: number, unreachable: number): string =>
      `${String(active)} active · ${String(healthy)} healthy · ${String(maintenance)} in maintenance · ${String(unreachable)} unreachable`,
    noneMatch: 'Nothing matches that search.',
    emptyTitle: 'No applications registered',
    emptyBody:
      'Register one so people can be sent to it and so it can report whether it is up.',
    emptyAction: 'Register an application',
    keyLocked: 'Permanent key — locked',
    activeLabel: 'Active',
    inactiveLabel: 'Inactive',
    edit: 'Edit',
    baseAddress: 'Base address',
    version: 'Version',
    lastChecked: 'Last checked',
    holdersTitle: 'People with access',
    grantToPerson: 'Grant to a person',
    noHolders: 'Nobody has access to this application yet.',
    selectPrompt: 'Choose an application on the left to see its details.',
    checkNow: 'Check now',
    /* ---- Credential — issue / rotate / revoke / test ---- */
    credentialTitle: 'Credential',
    credentialEmptyBody:
      'No credential has been issued. Nothing can connect through Accounts until one is.',
    issueCredential: 'Issue credential',
    rotateCredential: 'Rotate',
    revokeAllCredentials: 'Revoke all',
    /**
     * THE ROTATION RULE. Issuing a new credential does not touch the old one — both work at
     * once, on purpose, so an operator can cut a running application over without a gap.
     * Revoking is the ONLY thing that stops a secret; issuing never does.
     */
    rotateNote:
      'Issuing a new credential does not stop this one — both work until you revoke one. Paste the new secret into the application, confirm it connects with Test connection, then revoke the old credential to retire it.',
    credentialIssued: (at: string): string => `Issued ${at}`,
    credentialLastUsed: (at: string): string => `Last used ${at}`,
    /** A real and important answer — never a dash. */
    credentialNeverUsed: 'Never used',
    testConnection: 'Test connection',
    testConnectionPlaceholder: 'Paste the secret configured in the application',
    testConnectionHint: 'Tests whether this secret is a valid credential — not whether the application itself is reachable.',
    /**
     * THE LAUNCHER TILE CARD. Edited INLINE here with an explicit Save — deliberately not in
     * the Edit dialog, which owns identity (name, address, active) rather than presentation.
     * The logo is its own pair of endpoints and therefore applies IMMEDIATELY, exactly as
     * Settings → Branding's does; only the description and tags are part of the dirty form.
     */
    tile: {
      title: 'Launcher tile',
      subtitle: 'what everyone sees on the launcher',
      collapse: 'Collapse',
      expand: 'Expand',
      logoLabel: 'Logo',
      chooseLogo: 'Choose logo',
      replaceLogo: 'Replace',
      removeLogo: 'Remove',
      /**
       * PNG ONLY. The demo's own helper line offered SVG; the operator WITHDREW it and the
       * server accepts nothing else — it sniffs the magic bytes rather than trusting the
       * declared type, so this sentence and the file picker both say PNG and mean it.
       */
      logoHint:
        'PNG, up to 512 KB. The tile shows it in a small rounded square, scaled to fit — never cropped, never stretched. Until one is set, the tile shows the application’s initial instead.',
      descriptionLabel: 'Description',
      descriptionPlaceholder: 'One or two plain sentences, shown on the tile under the name.',
      descriptionHint:
        'Leave it empty and the tile simply omits the line — nothing renders in its place.',
      descriptionCount: (used: number, max: number): string => `${String(used)}/${String(max)}`,
      descriptionTooLong: (max: number): string =>
        `Keep the description to ${String(max)} characters or fewer.`,
      tag1Label: 'Tag 1',
      tag2Label: 'Tag 2',
      noTag: 'No tag',
      tagsHint:
        'Tags are neutral — no color, no swatch. The vocabulary is managed in Settings → Launcher tags: create, retire and restore.',
      /*
       * THE APPLICATION'S OWN COLOR (U2, operator ruling). It moved here from Settings —
       * one application, one place — and it is deliberately NOT called an "announcement
       * colour" any more: it is this application's color wherever the application appears,
       * which today is the announcement pills and the app chips on both Sessions screens.
       * A TYPE's color is a different fact and keeps its own home on Announcements → Types.
       */
      colorLabel: 'Pill color',
      colorHint:
        'The color this application’s chip wears everywhere it appears — announcements that target it, and the app chips on the Sessions screens. Colors come from the token set; the same ten swatches read correctly in both palettes, light and dark.',
      /** The swatch group's accessible name — it always says WHAT is being colored. */
      colorGroupLabel: (name: string): string => `Color for ${name}`,
      /** No swatch chosen is a real answer — the chip simply renders neutral. */
      noColor: 'No color assigned',
      /** The server refuses the same thing (400); the picker prevents it reaching that far. */
      sameTag: 'The two tag slots must hold different tags.',
      /** A retired tag stays on an application already carrying it; only NEW use is refused. */
      retiredTagOption: (name: string): string => `${name} (retired)`,
      reset: 'Reset to saved',
    },
    /** R3 — the four states. "Never checked" makes no status claim at all. */
    health: {
      healthyLabel: 'Healthy',
      healthyNote: 'Responding normally.',
      maintenanceLabel: 'Under maintenance',
      maintenanceNote:
        'The application reported planned downtime. People can still be granted access; sign-in resumes when it returns.',
      unreachableLabel: 'Unreachable',
      unreachableNote: 'No response at the last check. This is unplanned.',
      neverLabel: 'Never checked',
      neverNote: 'Registered, but it has not reported a status yet.',
      unknownLabel: 'Status reported',
      unknownNote: 'The application reported a status this screen does not recognize.',
    },
  },

  grants: {
    /**
     * Fed from `meta.summary` — every grant ever written, live and revoked, across the whole
     * collection. It is deliberately NOT the count for the person or application selected on
     * the left; that count is the list itself.
     */
    meta: (live: number, revoked: number): string =>
      `${String(live)} live grants · ${String(revoked)} revoked`,
    byPerson: 'By person',
    byApplication: 'By application',
    viewLabel: 'Grants view',
    searchPeople: 'Search name or email',
    searchApplications: 'Search applications',
    columnApplication: 'Application',
    columnPerson: 'Person',
    emptyForPerson: 'This person cannot open any application yet.',
    emptyForApplication: 'Nobody has access to this application yet.',
    grantApplication: 'Grant an application',
    grantToPerson: 'Grant to a person',
    allAppsHeld: 'This person already has every application.',
    allPeopleHold: 'Everyone active already has this application.',
    /** By-application view only — grants this one application to every active person at once. */
    bulkGrantAction: 'Grant to everyone active',
    bulkGrantTitle: (application: string): string => `Grant ${application} to everyone active?`,
    bulkGrantBody: (count: number): string =>
      `${count === 1 ? '1 active person does not yet have access' : `${String(count)} active people do not yet have access`} — they will all be granted it immediately. Each grant is recorded in the audit log, exactly like granting one at a time.`,
    bulkGrantSubmit: 'Grant to everyone',
    showRevoked: (count: number): string => `Show revoked history (${String(count)})`,
    hideRevoked: (count: number): string => `Hide revoked history (${String(count)})`,
    revokedLine: (at: string): string => `Revoked ${at}`,
    grantedBy: (at: string, by: string): string => `Granted ${at} by ${by}`,
    grantedOn: (at: string): string => `Granted ${at}`,
    selectPersonPrompt: 'Choose somebody on the left.',
    selectApplicationPrompt: 'Choose an application on the left.',
    noPeople: 'There is nobody to show yet.',
    noApplications: 'There are no applications to show yet.',
  },

  /* --------------------------------------------------------------------- audit -- */

  audit: {
    /**
     * The action-type filter is a GROUP, resolved server-side. `apps` spans two verb
     * namespaces and `operator` is an actor-type predicate, so neither is a prefix — the
     * labels here name what an operator is looking for, not what the verbs are called.
     */
    actionTypeLabel: 'Action type',
    groups: {
      all: 'All actions',
      users: 'User management',
      apps: 'Applications',
      grants: 'Access grants',
      /** Wave 3B, with the feature itself — labelled at birth rather than found missing
       *  later, the way D44 had to close the same gap for the session verbs. */
      announcements: 'Announcements',
      /** `sessions` is the routine sign-in lifecycle — never the two failure verbs, which are
       *  `security`'s exact membership (D32). */
      sessions: 'Sign-in activity',
      security: 'Security events',
      operator: 'Operator CLI',
      /**
       * Keyed `branding` until Phase 3 Wave 3A, when the key was renamed to match what the
       * server filter behind it always matched: every `settings.*` action, not only branding
       * ones. The old name dated from when branding was the only thing Settings held.
       */
      settings: 'Settings',
      /** Access wave 1 (SIR-7460 plan 3d): labels only, so the typed group map stays whole.
       *  The access screens themselves wait for the board's demo pick. */
      access: 'Access records and catalog',
      requests: 'Access requests',
      lifecycle: 'HR and lifecycle',
      /** Access wave 2: label only, so the typed group map stays whole. */
      deprovisioning: 'App deactivation sync',
    },
    searchLabel: 'Search actor or target',
    fromLabel: 'From',
    toLabel: 'To',
    fromFieldLabel: 'From date and time',
    toFieldLabel: 'To date and time',
    /**
     * Describes `meta.total` — the whole set the CURRENT filters match, server-side. Never
     * the loaded page, which is capped and would under-report. The separator is pinned to
     * en-GB for the same reason the dates are: every operator reads the same number.
     */
    count: (total: number): string =>
      total === 1 ? '1 event' : `${total.toLocaleString('en-GB')} events`,
    columns: {
      when: 'When',
      actor: 'Actor',
      action: 'Action',
      target: 'Target',
      ip: 'IP',
      view: 'View',
    },
    /** The row control's accessible name — every row's visible label is the same word. */
    viewEntry: (action: string): string => `View ${action} entry`,
    /** The button before a total is known (or when there is nothing to export). */
    exportCsv: 'Export CSV',
    /**
     * RULED WORDING — the button says what the export covers: the WHOLE filtered set,
     * `meta.total`, never the visible page. There is deliberately no dialog asking which.
     */
    exportAll: (total: number): string =>
      total === 1 ? 'Export 1 result' : `Export all ${total.toLocaleString('en-GB')} results`,
    exporting: 'Exporting…',
    /**
     * AN ENTRY WITH NO PRINCIPAL IS NOT A BROKEN ROW. Two cases are legitimate and both are
     * stated rather than blanked: the operator CLI runs on the host with no user session, and
     * a failed sign-in has an address but nobody behind it. Neither ever gets an invented name.
     */
    actorOperatorCli: 'Operator CLI',
    actorOperatorCliSub: 'no user session',
    actorNone: 'No actor',
    actorNoneSub: 'not signed in',
    /** Filters match nothing — deliberately NOT the same state as an empty log. */
    noMatch: 'Nothing matches these filters.',
    clearFilters: 'Clear filters',
    /** The log itself is empty. Warm and factual: nothing here may read as a fault. */
    emptyTitle: 'No events yet',
    emptyBody:
      'The log records every administrative and security event from here on — activity appears as it happens.',
    /**
     * Real pagination — ruled at 50 rows per page, matching the CLI default. "Page 2 of 7"
     * is the chosen form; both numbers come from `meta.total`, never from the rows in hand.
     */
    pageOf: (page: number, pages: number): string =>
      `Page ${page.toLocaleString('en-GB')} of ${pages.toLocaleString('en-GB')}`,
    previousPage: 'Previous',
    nextPage: 'Next',
    /** Relative stamps, in the design of record's own wording. */
    time: {
      justNow: 'Just now',
      minutesAgo: (minutes: number): string => `${String(minutes)}m ago`,
      hoursAgo: (hours: number): string => `${String(hours)}h ago`,
      yesterday: 'Yesterday',
      daysAgo: (days: number): string => `${String(days)} days ago`,
    },
    detail: {
      title: 'Audit entry details',
      stamp: (relative: string, absolute: string): string => `${relative} · ${absolute}`,
      actorLabel: 'Actor',
      contextLabel: 'Context',
      /** Some verbs record no metadata at all. Say so rather than showing an empty frame. */
      noContext: 'No further details were recorded for this event.',
      showRaw: 'Show raw',
      hideRaw: 'Hide raw',
      /** Both halves of the footer are honest when the fact is absent — never a stand-in. */
      ipKnown: (ip: string): string => `IP ${ip}`,
      ipUnknown: 'No address recorded',
      actingUser: (id: string): string => `acting user ${id}`,
      noActingUser: 'no acting user',
      footer: (address: string, actor: string): string => `${address} · ${actor}`,
    },
    /** Refusing an empty file is the honest outcome — an empty CSV looks like a broken export. */
    nothingToExport: 'Nothing to export with these filters.',
  },

  /* ------------------------------------------------------------- announcements -- */

  announcements: {
    /** The screen's two tabs. The list is the default; Types manages the color vocabulary. */
    tabList: 'Announcements',
    tabTypes: 'Types',
    tabsLabel: 'Announcements view',
    /**
     * The table's header line. Every number comes from the full list — this endpoint is
     * unpaginated, so the rows in hand ARE the whole collection and counting them is honest.
     * The three schedule counts come from the SERVER's own `scheduleState`, never from a
     * client-side reading of the timestamps.
     */
    meta: (live: number, scheduled: number, ended: number, off: number): string =>
      `${String(live)} live · ${String(scheduled)} scheduled · ${String(ended)} ended · ${String(off)} deactivated`,
    columns: {
      announcement: 'Announcement',
      schedule: 'Schedule',
      application: 'Application',
      type: 'Type',
      window: 'Window',
      actions: 'Actions',
    },
    /**
     * THE THREE SCHEDULE STATES, and the fourth row treatment that is not one of them.
     * `scheduleState` is DERIVED SERVER-SIDE and read here verbatim — no screen in this app
     * re-derives it from startsAt/endsAt. A manually deactivated announcement is off the
     * launcher whatever its schedule says, so it carries its own neutral label instead.
     */
    scheduleLive: 'Live',
    scheduleScheduled: 'Scheduled',
    scheduleEnded: 'Ended',
    scheduleOff: 'Off the launcher',
    /** The Window column. "Always on" is a real answer — an announcement needs no schedule. */
    windowAlways: 'Always on',
    windowStarts: (at: string): string => `starts ${at}`,
    windowEnds: (at: string): string => `ends ${at}`,
    /** D5 — an inactive announcement is RETIRED, never deleted, and stays listed here. */
    chipActive: 'On the launcher',
    chipRetired: 'Retired',
    /** A body-only announcement is normal; the table still needs a row label for it. */
    untitled: 'Untitled announcement',
    /**
     * Who wrote it and when, resolved by ID join. When the author record cannot be read the
     * line says so — never a stand-in name, never a blank that reads as data loss.
     */
    postedLine: (at: string, by: string): string => `Posted ${at} by ${by}`,
    postedLineNoAuthor: (at: string): string => `Posted ${at} — author record unavailable`,
    /** The list truncates a long body for scanning; the edit dialog always holds the whole. */
    truncatedBody: (text: string): string => `${text}…`,
    edit: 'Edit',
    deactivate: 'Deactivate',
    reactivate: 'Reactivate',
    emptyTitle: 'No announcements yet',
    emptyBody:
      'Post one and it appears at the top of everyone’s launcher — the page every signed-in person lands on.',
    /**
     * The Types tab. Types are OPTIONAL color-coded labels; an announcement without one
     * simply renders no type pill, and the table cell for it is left EMPTY — not a dash,
     * not the word "none".
     */
    types: {
      meta: (active: number, retired: number): string =>
        `${String(active)} active · ${String(retired)} retired`,
      columns: {
        type: 'Type',
        colour: 'Color',
        usedOn: 'Used on',
        state: 'State',
        actions: 'Actions',
      },
      stateActive: 'Active',
      stateRetired: 'Retired',
      edit: 'Edit',
      retire: 'Retire',
      newType: 'New type',
      emptyTitle: 'No types yet',
      emptyBody:
        'Types are optional labels — Maintenance, Release, Policy — that color-code announcements everywhere they appear. Announcements posted before any types exist simply carry no type pill.',
      /**
       * RETIRE, NEVER DELETE — and a retired announcement type is NOT restorable, unlike a
       * launcher tag. Its name and color render on every announcement already carrying it
       * exactly as they were retired, so editing or reviving one would rewrite history. The
       * screen says so where the Restore action would otherwise sit.
       */
      retiredNote: 'Kept on the announcements carrying it. Not offered for new ones.',
      /** Counted from the loaded announcement list, which IS the whole collection. */
      usedOnCount: (count: number): string => String(count),
    },
  },

  settings: {
    /** The section menu (SIR-7804), the same component the Access hub draws. */
    menu: {
      label: 'Settings sections',
      general: 'General',
      operations: 'Operations',
      email: 'Email & notifications',
      access: 'Access',
    },
    /** Settings → Email & notifications (SIR-7804), ported from BundleUp's section. */
    email: {
      statusKey: 'SendGrid API key',
      keyConfigured: (last4: string | null): string =>
        last4 === null ? 'Configured' : `Configured, ending ${last4}`,
      keyMissing: 'Not set',
      statusMaster: 'Master switch',
      on: 'On',
      off: 'Off',
      statusFrom: 'From',
      captureNote:
        'This environment captures email instead of sending it. Nothing leaves the server, and a test email reports skipped.',
      title: 'Email & notifications',
      masterLabel: 'Send emails org-wide',
      masterHint: 'When off, no email of any type is sent.',
      keyLabel: 'SendGrid API key',
      keyPlaceholderSet: (last4: string | null): string =>
        last4 === null
          ? 'A key is set. Type a new one to replace it.'
          : `Key ending ${last4} is set. Type a new one to replace it.`,
      keyPlaceholderMissing: 'Paste a SendGrid API key',
      keyHint:
        'Stored encrypted and never shown again once saved. Leave empty to keep the current key.',
      clearKey: 'Clear saved key',
      keepKey: 'Keep saved key',
      clearKeyPending:
        'The saved key is removed when you save. The server environment key applies if one is set.',
      fromLabel: 'From address',
      fromHint: 'Must be a SendGrid-verified sender.',
      fromRequired: 'Enter a From address.',
      senderLabel: 'Sender name',
      senderRequired: 'Enter a sender name.',
      replyToLabel: 'Reply-To',
      replyToHint: 'Replies go to the From address when this is empty.',
      eventsTitle: 'Events',
      eventsHint: 'Email and In-app are set per event. In-app is the notification bell.',
      columns: { event: 'Event', email: 'Email', inApp: 'In-app' },
      noEvents: 'No notification events.',
      emailFor: (event: string): string => `Email for ${event}`,
      inAppFor: (event: string): string => `In-app for ${event}`,
      testTitle: 'Send test email',
      testHint: 'Sends to your own address using the saved setup.',
      testButton: 'Send test email',
      testSending: 'Sending…',
      testSaveFirst: 'You have unsaved changes. The test uses the saved setup.',
      testSent: (recipient: string): string => `Test email sent to ${recipient}.`,
      testSkipped: {
        capture_mode: (recipient: string): string =>
          `Not sent to ${recipient}: this environment captures email instead of sending it.`,
        master_disabled: (recipient: string): string =>
          `Not sent to ${recipient}: Send emails org-wide is off.`,
        api_key_not_configured: (recipient: string): string =>
          `Not sent to ${recipient}: no SendGrid API key is set.`,
      },
    },
    access: {
      title: 'Access',
      overdueLabel: 'App deactivation overdue after',
      overdueHint:
        'An open app deactivation older than this shows as overdue in the Access queue and on offboarding checklists.',
      hoursUnit: 'hours',
      overdueRange: (min: number, max: number): string =>
        `Enter a whole number of hours from ${String(min)} to ${String(max)}.`,
      switchesTitle: 'What HR and IT can see',
      switchesLead:
        'Administrators always have all of these. Each switch is separate; all are off until you turn them on.',
      switchesCaption: 'Access switches for HR and IT',
      switchColumnHr: 'HR',
      switchColumnIt: 'IT',
      switchSeeMatrix: 'See Matrix',
      switchExportMatrix: 'Export Matrix',
      switchSeeCatalog: 'See Catalog',
      switchSeeCatalogHint: 'View only. Only administrators edit the Catalog.',
      switchLabel: (row: string, role: string): string => `${row}: ${role}`,
      exportNeedsSee: (role: string): string =>
        `${role}: Export Matrix has no effect until See Matrix is on for ${role} too.`,
    },
    brandingTitle: 'Branding',
    logoLabel: 'Logo',
    chooseLogo: 'Choose logo',
    removeLogo: 'Remove',
    logoHint:
      'PNG, JPEG or WebP, up to 512 KB, displayed 32px tall. SVG is not accepted: an SVG served from this origin can carry script, which would run on the sign-in page. Until a logo is set, the brand letter is shown.',
    nameLabel: 'Product name',
    nameHint: 'Appears beside the logo on the sign-in page and in every application header.',
    taglineLabel: 'Tagline',
    taglineHint: 'The statement on the sign-in brand panel. Leave empty to hide it.',
    subLabel: 'Supporting line',
    glyphLabel: 'Brand letter',
    glyphHint:
      'Shown oversized and faint on the sign-in panel. When empty, the first letter of the product name is used.',
    optional: 'optional',
    /**
     * Deliberately "discard", not "reset to defaults": the product defaults live on the
     * server and are not exposed by any endpoint, and a client-side copy of them would mean
     * hard-coding the product name — which R2 forbids outright. So this reverts to what is
     * saved, which is a thing the client can actually know.
     */
    discardChanges: 'Discard changes',
    unsaved: 'Unsaved changes',
    save: 'Save changes',
    saving: 'Saving…',
    nameRequired:
      'The product name is required — it appears everywhere the suite says its own name.',
    /**
     * The preview disclosure inside the Branding card (Wave 3B fix round) — the Grants
     * screen's revoked-history row is the pattern precedent. Collapsed on every fresh
     * visit; nothing persists the open state.
     */
    showPreview: 'Show preview',
    hidePreview: 'Hide preview',
    previewNote:
      'The sign-in page brand panel, rendered from the values above. Updates as you type; applies when saved.',
    /**
     * RULING 1 — the operational settings are real, saved-to-the-database settings, so they
     * get a reachable control here rather than requiring a psql session. Mirrors the
     * branding section above exactly: same field/label/hint treatment, same dirty-tracked
     * save, same primitives. Bounds are stated as affordance — the server enforces them
     * independently and its own refusal message is what actually renders on a bad value.
     *
     * Renamed from "Health polling" in Wave 3B, when the card gained session lifetime and
     * the introspection cache — a card holding all four is not honestly "Health polling".
     */
    operationalTitle: 'Operations',
    intervalLabel: 'Check interval',
    intervalHint:
      'How often Accounts checks each active application, in seconds. From 15 seconds up to 3600 (one hour).',
    intervalInvalid: 'Enter a whole number of seconds between 15 and 3600.',
    thresholdLabel: 'Failure threshold',
    /** The sentence an operator most needs and will not infer from the field name alone. */
    thresholdHint:
      'How many consecutive failed checks it takes before an application is reported unreachable — so one momentary timeout does not turn a healthy application red. From 1 to 10.',
    thresholdInvalid: 'Enter a whole number between 1 and 10.',
    sessionTtlLabel: 'Session lifetime',
    /**
     * Backend fact, must not be overpromised: the value applies to sessions issued from the
     * NEXT sign-in onward — an already-issued session keeps the lifetime it was issued
     * with. The sentence names the control that actually cuts access off immediately, so an
     * operator reaching for this field in a hurry is pointed at the right one.
     */
    sessionTtlHint:
      'How long a sign-in lasts before the person must sign in again, in days — from 1 up to 365. Applies to sessions issued from now on; existing sessions keep the lifetime they were issued with. To end someone’s access immediately, sign them out everywhere or deactivate them instead.',
    sessionTtlInvalid: 'Enter a whole number of days between 1 and 365.',
    introspectionLabel: 'Introspection cache',
    /**
     * OPERATOR-RULED WORDING (D24). This number IS the suite's deprovisioning window, and
     * the sentence must say so plainly, in both directions, for a non-technical
     * administrator — an operator changing it is deciding how long a removed person keeps
     * working, and softening that would hide the decision they are actually making.
     */
    introspectionHint:
      'How long applications may trust their last answer from Accounts before asking again, in seconds. This is the longest a person whose access has been revoked can keep working inside an application before they are locked out — lowering this number shortens that window, and raising it lengthens it. From 30 seconds up to 3600 (one hour).',
    introspectionInvalid: 'Enter a whole number of seconds between 30 and 3600.',
    auditPageSizeLabel: 'Audit rows per page',
    /**
     * A SCREEN preference, and the sentence says which screen so nobody expects it to reach
     * further: the operator CLI's own default (50) and the API's limit are deliberately
     * independent of this setting. The 200 ceiling is the API's own maximum page.
     */
    auditPageSizeHint:
      'How many rows the audit log screen shows per page. From 10 up to 200. Applies to the admin panel’s audit screen only — the operator CLI keeps its own default.',
    auditPageSizeInvalid: 'Enter a whole number of rows between 10 and 200.',
    announcementClampLinesLabel: 'Announcement lines shown',
    /**
     * A PRESENTATION preference, and the sentence has to say so: nothing about the
     * announcement changes, only how much of it the launcher shows before the link. Naming
     * the link is what makes the setting findable from the screen it governs.
     */
    announcementClampLinesHint:
      'How many lines of an announcement the launcher shows before the View more link. From 1 up to 8. Nothing is lost either way — the full announcement is always one click away.',
    announcementClampLinesInvalid: 'Enter a whole number of lines between 1 and 8.',
    sessionPurgeDaysLabel: 'Session purge window',
    /**
     * The non-obvious part, stated plainly (per the brief for this field): a purged row is
     * not just tidying — it is also how long PER-DEVICE sign-out can still reach that
     * session. Past this window, ending it means sign-out-everywhere or deactivation.
     */
    sessionPurgeDaysHint:
      'How long a dead session — signed out or expired — is kept before the operator’s purge-sessions command removes it, in days. From 1 up to 365. It is also how long per-device sign-out can still reach that session; once it is purged, ending it means signing out everywhere or deactivating the account instead.',
    sessionPurgeDaysInvalid: 'Enter a whole number of days between 1 and 365.',
    /**
     * OPERATOR-RULED WORDING — the 2FA wave. Drift is explained in plain time, never in
     * TOTP jargon; the ceiling of 2 is the server's own hard bound (beyond it the window
     * stops being drift tolerance and becomes a replay window), so the sentence names 2 as
     * the loosest allowed rather than implying the number is free.
     */
    totpDriftLabel: 'Code drift tolerance',
    totpDriftHint:
      'How many 30-second windows either side of now a code is accepted for. 1 means a code stays valid for up to 90 seconds in total; 0 is strictest and will fail codes from phones whose clocks drift; 2 is the loosest allowed.',
    totpDriftInvalid: 'Enter 0, 1 or 2.',
    totpRequirementLabel: 'Two-step requirement',
    /**
     * Two facts an operator must not have to infer: an enrolled account is always
     * challenged whatever this is set to, and a required-but-unenrolled account is held at
     * set-up — it cannot enter anything until enrollment finishes.
     */
    totpRequirementHint:
      'Whether people must use two-step verification. Anyone who has already set it up is asked for a code at every sign-in, whatever this is set to. Requiring it holds the affected people at set-up the next time they sign in — they cannot open anything until they finish.',
    totpRequirementOptions: {
      optional: 'Optional',
      required_admins: 'Required for administrators, HR and IT',
      required_all: 'Required for everyone',
    },
    /**
     * U5 — THE RATE LIMIT CARD, real at last: six live-read settings behind the same
     * operational PUT, saved as a unit exactly like the Operations card above.
     *
     * OPERATOR-RULED HONESTY, and the reason the lead is worded the way it is: the limiter
     * is an IN-PROCESS counter (server/src/lib/rate-limit.ts). Each Accounts process counts
     * on its own and the counts are never shared or summed, so these numbers bound ONE
     * instance. The copy must not imply a suite-wide or cluster-wide ceiling the
     * implementation does not deliver — softening that would be a promise nothing keeps.
     *
     * Bounds are stated as affordance only. This card deliberately runs NO submit-time
     * check of its own: the server's bounds are the real ones and its refusal is what
     * renders, so the screen can never invent a stricter rule than the one being enforced.
     */
    rateLimitsTitle: 'Rate limits',
    rateLimitsLead:
      'Counted per server instance, in memory. Each Accounts process keeps its own tally and the tallies are never shared or added together, so these numbers bound one instance — not the suite as a whole. There is one instance today. A change binds the very next request; no restart.',
    loginRateLimitLabel: 'Failed sign-ins per email address',
    loginRateLimitHint:
      'How many failed sign-in attempts one email address may make inside the window below before Accounts starts refusing. The ceiling per network address is five times this number. From 1 up to 10000.',
    loginRateLimitWindowLabel: 'Sign-in window',
    loginRateLimitWindowHint:
      'How long failed sign-in attempts are remembered for, in seconds. From 1 up to 86400 (one day).',
    introspectRateLimitLabel: 'Introspection calls per application',
    introspectRateLimitHint:
      'How many successfully authenticated introspection calls one application may make inside the window below. Applications ask on every request they cannot answer from their cache, so this needs real headroom. From 1 up to 100000.',
    introspectRateLimitWindowLabel: 'Introspection window',
    introspectRateLimitWindowHint:
      'How long introspection calls are counted for, in seconds. From 1 up to 86400 (one day).',
    introspectFailureRateLimitLabel: 'Failed introspection attempts per address',
    introspectFailureRateLimitHint:
      'How many introspection calls that fail to authenticate one source address may make inside the window below — the guard against somebody spraying guessed application secrets. From 1 up to 10000.',
    introspectFailureRateLimitWindowLabel: 'Failed introspection window',
    introspectFailureRateLimitWindowHint:
      'How long failed introspection attempts are remembered for, in seconds. From 1 up to 86400 (one day).',

    /**
     * THE LAUNCHER TAG VOCABULARY. Built to the announcement type manager's pattern exactly,
     * MINUS the color column: tags are NEUTRAL by ruling — no color, no swatch, ever.
     * Unlike a type, a retired tag IS restorable (and stays renameable while retired),
     * which is why this manager has a Restore action and the type manager does not.
     */
    tags: {
      title: 'Launcher tags',
      lead: 'Short neutral labels an application can carry on its launcher tile — two at most, chosen on the application’s Launcher tile card. Tags never carry a color.',
      meta: (active: number, retired: number): string =>
        `${String(active)} active · ${String(retired)} retired`,
      columns: {
        tag: 'Tag',
        usedOn: 'Used on',
        state: 'State',
        actions: 'Actions',
      },
      stateActive: 'Active',
      stateRetired: 'Retired',
      edit: 'Edit',
      retire: 'Retire',
      restore: 'Restore',
      newTag: 'New tag',
      emptyTitle: 'No tags yet',
      emptyBody:
        'Create one and it becomes available on every application’s Launcher tile card. Applications work perfectly well without any.',
    },
  },

  /* -------------------------------------------------------------------- modals -- */

  modals: {
    createUser: {
      title: 'Create user',
      lead: 'They will get a temporary password, shown once on the next screen.',
      nameLabel: 'Full name',
      namePlaceholder: 'Ops Lead',
      emailLabel: 'Email address',
      emailPlaceholder: 'ops.lead@20free.net',
      adminTitle: 'Make this person an administrator',
      adminNote:
        'Administrators can create people, register applications and change anyone’s access.',
      submit: 'Create user',
      required: 'Name and email address are both required.',
    },
    temporaryPassword: {
      titleCreated: 'User created',
      titleReset: 'Temporary password issued',
      leadCreated: (name: string): string =>
        `${name} can now sign in with this temporary password. They will be asked to choose a new one straight away.`,
      leadReset: (name: string): string =>
        `Give this to ${name}. They will be asked to choose a new one when they next sign in.`,
      banner: 'Temporary password — shown once',
      copy: 'Copy password',
      copied: 'Copied to clipboard',
      finality:
        'Copy it now. Once you close this window it is gone — it is not stored anywhere in a readable form, and it cannot be shown again. If it is lost, reset the password to issue a new one.',
      mustChangeChip: 'Must change at next sign-in',
      mustChangeSuffix: (name: string): string => `is now set on ${name}.`,
      done: 'I have copied it — close',
    },
    deactivate: {
      title: (name: string): string => `Deactivate ${name}?`,
      body: 'They will be signed out everywhere and will not be able to sign in again. Their access grants are kept, so reactivating restores everything exactly as it was.',
      submit: 'Deactivate',
    },
    refuseDeactivate: {
      title: (name: string): string => `${name} cannot be deactivated`,
      strong: 'This is the last active administrator.',
      body: 'If this account were deactivated, nobody could create people, register applications or restore access — including you.',
      after:
        'Make somebody else an administrator first, then come back and deactivate this account.',
    },
    refuseDemote: {
      title: (name: string): string => `${name} must stay an administrator`,
      strong: 'This is the last active administrator.',
      body: 'Removing the administrator role here would leave nobody able to create people, register applications or restore access — including you.',
      after: 'Make somebody else an administrator first, then come back and change this one.',
    },
    resetPassword: {
      title: (name: string): string => `Reset the password for ${name}?`,
      body: 'Their current password stops working immediately. A new temporary password is generated and shown once on the next screen — you will need to pass it to them yourself.',
      submit: 'Reset password',
    },
    /**
     * The lost-phone path — RULED COPY. This dialog is the only place the consequences are
     * stated, and all three are stated in plain words: two-step turns OFF, the recovery
     * codes die with it, and every live session ends. The closing sentence is the demo's
     * own use-case line; the warning callout is the demo's, verbatim.
     */
    resetTwoFactor: {
      title: (name: string): string => `Reset two-step for ${name}?`,
      body: 'Their authenticator app and recovery codes stop working immediately, and they are signed out of every device. Two-step is off for their account until they set it up again — their password alone signs them in. Use this when somebody has lost or replaced their phone.',
      warning: 'Until they enroll again, their account is protected by password only.',
      submit: 'Reset two-step',
    },
    signOutEverywhere: {
      title: (name: string): string => `Sign ${name} out everywhere?`,
      /**
       * Ruling 1 — this app has no session store, so the dialog can never claim a count and
       * must never say "no active sessions": both would be assertions nothing can back.
       */
      body: 'Any sessions they currently have will end immediately, in every application. Their password and access are unchanged — they can sign straight back in.',
      submit: 'Sign out everywhere',
    },
    /**
     * The same dialog turned on yourself, from the launcher's account menu. Ruling 1 again:
     * no session count is claimed, in either direction — this app has no session store, so
     * "you are signed in on 3 devices" and "no active sessions" are equally unbackable. The
     * one thing this version adds is that THIS session goes too, which is true and is the
     * consequence somebody needs to know before they confirm.
     */
    signOutEverywhereSelf: {
      title: 'Sign out everywhere?',
      body: 'Any sessions you currently have end immediately, in every application — including this one. Your password and access stay as they are.',
      submit: 'Sign out everywhere',
    },
    /**
     * Changing your own password while signed in and NOT walled — the wall has its own full
     * screen (`changePassword`). The server re-issues this session's cookie with the bumped
     * version in the same response, so this device stays signed in and every other one does
     * not. The length rule is THIS APP's 12 (`STR.changePassword.lengthHint`), reused rather
     * than restated: the application's rule wins over any number a demo happens to show.
     */
    changeOwnPassword: {
      title: 'Change password',
      lead: (email: string): string =>
        `Choose a new password for ${email}. You stay signed in on this device.`,
      currentLabel: 'Current password',
      newLabel: 'New password',
      confirmLabel: 'Confirm new password',
      submit: 'Change password',
      currentRequired: 'Enter your current password.',
    },
    editUser: {
      title: 'Edit user',
      /**
       * C6c — this is accurate, not reassurance. IDENTITY IS A FOREIGN KEY: everything joins
       * on the uuid, so re-labelling the address changes only how they sign in.
       */
      lead: 'Changing the email address changes how this person signs in. Their password and access grants are untouched.',
      nameLabel: 'Full name',
      emailLabel: 'Email address',
      /** C6 — display-only directory metadata. Never an authorization input. */
      titleLabel: 'Title',
      titlePlaceholder: 'Operations lead',
      departmentLabel: 'Department',
      departmentPlaceholder: 'Operations',
      adminTitle: 'Administrator',
      adminNote:
        'Administrators can create people, register applications and change anyone’s access. There must always be at least one.',
      /**
       * D2 — the server refuses promoting a deactivated user (400 `inactive_target`), so the
       * control must not offer it. Stated rather than hidden: a vanished checkbox puzzles,
       * a sealed one with a reason teaches. Demotion stays available, which is why this
       * appears only for a deactivated person who is NOT already an administrator.
       */
      adminInactive:
        'Reactivate this person first — a deactivated account cannot be made an administrator.',
      required: 'Name and email address are both required.',
      unchanged: 'Nothing has been changed yet.',
    },
    /**
     * Self-demotion is ALLOWED (operator ruling). It is not refused — it is confirmed, in
     * plain words, because the consequence is immediate and cannot be undone from here.
     */
    confirmSelfDemote: {
      title: 'Remove your own administrator role?',
      body: 'You lose the admin panel the moment this saves — this screen closes and you go back to your account. Your sign-in, your password and your application access are unchanged.',
      after:
        'Another administrator, or the operator CLI on the server, can give the role back. You cannot give it back to yourself.',
      submit: 'Remove my administrator role',
    },
    /**
     * THE SHOWN-ONCE APPLICATION SECRET. Same shape as `temporaryPassword` above — a banner,
     * a lead that differs between a first issue and a rotation, a finality line — plus the
     * one thing a password dialog does not need: an explicit acknowledgement, because there
     * is no "reset it" recovery here. Losing this secret means issuing a whole new credential.
     */
    issueCredential: {
      titleIssued: 'Credential issued',
      titleRotated: 'New credential issued',
      leadIssued: (name: string): string =>
        `${name} can now authenticate with Accounts using this secret.`,
      leadRotated: (name: string): string =>
        `A new secret for ${name}. The previous credential keeps working until you revoke it.`,
      banner: 'Application secret — shown once',
      copy: 'Copy secret',
      copied: 'Copied to clipboard',
      finality:
        'Copy it now. Once you close this window it is gone — it is not stored anywhere in a readable form, and it cannot be shown again. If it is lost, issue a new credential and reconfigure the application.',
      /** D12 — the application is switched off suite-wide; nothing will be admitted with it. */
      inactiveWarning:
        'This application is currently switched off suite-wide. The credential is issued, but nothing will be admitted until the application is reactivated.',
      acknowledgeLabel: 'I have copied the secret',
      done: 'Done',
    },
    revokeCredential: {
      title: (application: string): string => `Revoke this credential for ${application}?`,
      body: 'Anything still using this secret is refused immediately. If this is the only credential, the application cannot call Accounts at all until a new one is issued and configured.',
      submit: 'Revoke',
    },
    revokeAllCredentials: {
      title: (application: string): string => `Revoke every credential for ${application}?`,
      body: 'Every secret issued for this application stops working immediately — it cannot call Accounts at all until a new credential is issued and configured.',
      submit: 'Revoke all',
    },
    registerApplication: {
      title: 'Register application',
      lead: 'Once registered, people can be granted access and the application can report whether it is up.',
      keyLabel: 'Key',
      keyPermanent: 'Permanent — cannot be changed later',
      keyPlaceholder: 'reports',
      keyHint:
        'Lower case, starting with a letter; letters, digits and hyphens only. The application identifies itself with this, so it can never be edited or reused.',
      nameLabel: 'Display name',
      namePlaceholder: 'Reports',
      urlLabel: 'Base address',
      urlPlaceholder: 'https://reports.20free.net',
      submit: 'Register',
      required: 'Key, display name and base address are all required.',
    },
    editApplication: {
      title: 'Edit application',
      keyLabel: 'Key',
      keyLocked: 'Locked',
      nameLabel: 'Display name',
      urlLabel: 'Base address',
      activeTitle: 'In use',
      activeNote:
        'Turn this off to retire the application. Existing grants are kept but nobody can open it.',
      required: 'Display name and base address are both required.',
    },
    grant: {
      title: 'Grant access',
      contextForPerson: (name: string): string => `Choose an application for ${name}.`,
      contextForApplication: (name: string): string =>
        `Choose a person to give access to ${name}.`,
      pickApplication: 'Application',
      pickPerson: 'Person',
      note: 'Access takes effect immediately and is recorded against your name in the audit log.',
      submit: 'Grant access',
      required: 'Choose one first.',
    },
    revoke: {
      title: (what: string, who: string): string => `Revoke ${what} for ${who}?`,
      body: 'They lose access straight away and are signed out of that application. The grant stays in the revoked history with today’s date, and you can grant it again at any time.',
      submit: 'Revoke access',
    },
    /**
     * One dialog for posting and editing — same fields, different verbs. There is no
     * "active" control in here: activation is its own pair of endpoints, reached from the
     * row's Deactivate/Reactivate, and a new announcement is posted live.
     */
    announcement: {
      titlePost: 'Post announcement',
      titleEdit: 'Edit announcement',
      lead: 'Appears at the top of the launcher for everyone it reaches. The body is markdown, rendered by the server — the preview shows exactly what the launcher will show.',
      titleLabel: 'Title',
      titlePlaceholder: 'Maintenance Saturday morning',
      bodyLabel: 'Body',
      bodyPlaceholder: 'Plain sentences are fine as-is.',
      /** The affordance counter against the server's own 2000-character bound. */
      count: (used: number): string => `${String(used)} / 2000`,
      /**
       * THE FORMATTING THE SERVER ACTUALLY RENDERS, and nothing else. Its renderer supports
       * exactly three inline constructs plus verbatim line structure — there are no lists,
       * so this sentence must never offer them. (The frozen demo's own helper line did; the
       * operator ruled it out, because copy that promises a construct the renderer drops is
       * worse than copy that promises nothing.)
       */
      bodyHint:
        'Markdown works if you want it — **bold**, *italic*, [link](https://…). Plain text needs no symbols; line breaks are kept exactly as you type them. Up to 2000 characters.',
      bodyRequired: 'The body is required — write what people need to know.',
      bodyTooLong: 'Use at most 2000 characters.',
      /** Targeting. Null = untargeted = suite-wide; that is the default and it is stated. */
      applicationLabel: 'Application',
      applicationNone: 'Not targeted — everyone sees it',
      everyoneTitle: 'Show everyone',
      everyoneNote:
        'Also shown to people without access to this application. Off: only people who can open it see it.',
      everyoneDisabledNote:
        'Pick an application first — an untargeted announcement already reaches everyone.',
      typeLabel: 'Type',
      /** Non-selectable placeholder — a type is required (D101), so the select must never
       *  silently default to the first real option while state still holds ''. */
      typeChoose: 'Choose a type…',
      /** Affordance only — the server refuses an untyped announcement independently (D101). */
      typeRequired: 'Choose a type before saving.',
      /** A retired type stays on announcements carrying it and is refused for new ones. */
      typeHint: 'Retired types stay on old announcements but are not offered here.',
      typeNoneYet: 'No types yet',
      typeNoneYetHint:
        'Create a type under Announcements → Types before you can post — every announcement now needs one.',
      startsLabel: 'Starts',
      endsLabel: 'Ends',
      scheduleHint:
        'Empty = on now, on until deactivated. A future start queues it; a past end takes it off.',
      scheduleInvalid: 'Enter a valid date and time, or leave the field empty.',
      /** Affordance only — the server refuses the same thing independently. */
      scheduleBackwards: 'The end time must be after the start time.',
      previewLabel: 'Launcher preview',
      /**
       * There is deliberately NO schedule pill in this preview, unlike the frozen demo's.
       * Two reasons, and they point the same way: schedule state is derived by the SERVER
       * and no screen in this app re-derives it (which is the only way a live preview pill
       * could work), and the launcher itself never draws one — so leaving it out makes the
       * preview a truer picture of what people actually see. The Schedule column on the
       * list is where an administrator reads that state, from the server's own answer.
       */
      previewNote: 'The launcher shows live announcements only — schedule state is on the list.',
      /**
       * THE ONE HONEST THING TO SAY about an unsaved body. The server is this app's only
       * markdown renderer — there is no client-side one, by ruling — so until a save lands
       * there is no rendered HTML to show and the preview shows the text exactly as typed.
       */
      previewUnrendered: 'Formatting is applied when you save; until then this is your text as typed.',
      /** Fallbacks so the preview keeps its shape while the fields are still empty. */
      previewTitleFallback: 'Untitled announcement',
      previewBodyFallback: 'Start typing — the preview fills in as you write.',
      submitPost: 'Post announcement',
      submitEdit: 'Save changes',
    },
    deactivateAnnouncement: {
      title: 'Deactivate this announcement?',
      body: 'It disappears from everyone’s launcher immediately. It stays in this list as retired and can be reactivated at any time — nothing in Accounts is ever deleted.',
      submit: 'Deactivate',
    },
    /**
     * THE REACTIVATION REFUSAL. The SERVER refuses (400) an announcement whose end time is
     * already past — reactivating it would "succeed" into permanent invisibility. This
     * dialog renders the server's own sentence and offers the only thing that fixes it.
     * The screen never pre-empts the refusal by re-deriving the window itself.
     */
    reactivateEnded: {
      title: 'This announcement has ended',
      body: 'Reactivating will not put it back on the launcher: the schedule decides what is live.',
      hint: 'Edit it and set a new end time — or clear the end time entirely — and it goes live again on its own.',
      submit: 'Edit the schedule',
    },
    /** New / edit announcement type. Color comes from the ten-swatch token set, never free. */
    announcementType: {
      titleNew: 'New type',
      titleEdit: 'Edit type',
      lead: 'Types color-code announcements everywhere they appear. Colors come from the token set — the same swatch reads correctly in both palettes, light and dark.',
      nameLabel: 'Name',
      namePlaceholder: 'Maintenance',
      nameRequired: 'A name is required.',
      colourLabel: 'Color',
      colourGroupLabel: 'Type color',
      previewLabel: 'Preview:',
      previewFallback: 'Type name',
      submitNew: 'Create type',
      submitEdit: 'Save changes',
    },
    retireAnnouncementType: {
      title: (name: string): string => `Retire ${name}?`,
      /**
       * The count is the announcements already carrying it, counted off the loaded list —
       * which is the whole collection, so the number is honest. RETIRE, NEVER DELETE: their
       * pills keep the type's name and color forever.
       */
      body: (used: number): string =>
        used === 1
          ? 'The 1 announcement already carrying it keeps its pill, color and all. It stops being offered for new announcements.'
          : `The ${String(used)} announcements already carrying it keep their pill, color and all. It stops being offered for new announcements.`,
      /** Stated plainly because it is the asymmetry with launcher tags, which DO come back. */
      permanent:
        'A retired type cannot be restored or edited — history renders it exactly as it is now. Reuse the name on a new type instead.',
      submit: 'Retire',
    },
    /** New / edit launcher tag. NEUTRAL — there is no color control here, by ruling. */
    applicationTag: {
      titleNew: 'New tag',
      titleEdit: 'Edit tag',
      lead: 'A short neutral label an application can carry on its launcher tile. Tags never carry a color.',
      nameLabel: 'Name',
      namePlaceholder: 'Internal',
      nameRequired: 'A name is required.',
      submitNew: 'Create tag',
      submitEdit: 'Save changes',
    },
    retireApplicationTag: {
      title: (name: string): string => `Retire ${name}?`,
      body: (used: number): string =>
        used === 1
          ? 'The 1 application already carrying it keeps it. It stops being offered for new tagging.'
          : `The ${String(used)} applications already carrying it keep it. It stops being offered for new tagging.`,
      restorable: 'You can restore it at any time.',
      submit: 'Retire',
    },
  },

  /* --------------------------------------------------------------------- toast -- */

  toast: {
    userCreated: (name: string): string => `User created — ${name}`,
    userDeactivated: (name: string): string =>
      `${name} deactivated — they can no longer sign in.`,
    userReactivated: (name: string): string => `${name} reactivated.`,
    userUpdated: (name: string): string => `${name} updated.`,
    passwordReset: (name: string): string => `Password reset for ${name}`,
    /** Your own, from the account menu. Says nothing about other devices — see Ruling 1. */
    passwordChanged: 'Password changed.',
    signedOutEverywhere: (name: string): string => `${name} signed out of every device.`,
    /* ---- sessions ---- */
    /** Self-service, per-device. */
    deviceSignedOut: 'That device is signed out.',
    /** Self-service, "Sign out everywhere else". */
    otherSessionsSignedOut: 'Signed out everywhere else — only this device remains.',
    /** Admin, per-device — the group-level "sign out of all" reuses `signedOutEverywhere`. */
    adminDeviceSignedOut: (name: string, device: string): string =>
      `${name}’s ${device} is signed out.`,
    nowAdmin: (name: string): string => `${name} is now an administrator.`,
    nowMember: (name: string): string => `${name} is no longer an administrator.`,
    applicationRegistered: (name: string): string =>
      `Registered ${name} — status appears once it reports one.`,
    applicationUpdated: 'Application updated.',
    applicationChecked: 'Checked — status updated.',
    credentialRevoked: 'Credential revoked.',
    credentialsRevokedAll: 'Every credential revoked — issue a new one to reconnect.',
    /**
     * Reports the real outcome, per `BulkGrantResponse` — never what was merely requested.
     * `granted === 0` is reachable by a race (somebody else granted the rest between the
     * confirmation opening and this submitting) even though the button is only offered when
     * the screen believes there is at least one person to grant — so it gets its own honest
     * sentence rather than the odd "Granted to 0 people."
     */
    bulkGranted: (granted: number, alreadyHeld: number): string => {
      if (granted === 0) return 'Nobody needed to be granted — everyone already had access.';
      const grantedPart =
        granted === 1 ? 'Granted to 1 person' : `Granted to ${String(granted)} people`;
      if (alreadyHeld === 0) return `${grantedPart}.`;
      const heldPart =
        alreadyHeld === 1 ? '1 already had access' : `${String(alreadyHeld)} already had access`;
      return `${grantedPart}; ${heldPart}.`;
    },
    accessGranted: (who: string, what: string): string => `Access granted — ${who} → ${what}`,
    accessRevoked: (who: string, what: string): string => `Access revoked — ${who} → ${what}`,
    /** Reports what actually landed in the file, never what was asked for. */
    auditExported: (count: number): string =>
      count === 1
        ? 'Exported 1 event to CSV.'
        : `Exported ${count.toLocaleString('en-GB')} events to CSV.`,
    announcementPosted: 'Announcement posted — it is on everyone’s launcher now.',
    announcementUpdated: 'Announcement updated.',
    announcementDeactivated: 'Announcement retired — it no longer appears on the launcher.',
    announcementReactivated: 'Announcement reactivated — it is back on everyone’s launcher.',
    /* ---- announcement types ---- */
    typeCreated: (name: string): string => `Type created — ${name}.`,
    typeUpdated: 'Type updated.',
    typeRetired: (name: string): string =>
      `${name} retired — kept on old announcements, refused for new ones.`,
    /* ---- launcher tags ---- */
    tagCreated: (name: string): string => `Tag created — ${name}.`,
    tagUpdated: 'Tag updated.',
    tagRetired: (name: string): string => `${name} retired — not offered for new tagging.`,
    tagRestored: (name: string): string => `${name} restored — offered again.`,
    /* ---- launcher tile (the application's own color rides this same card, U2) ---- */
    tileSaved: 'Launcher tile saved — the launcher shows it now.',
    tileDiscarded: 'Unsaved launcher tile changes discarded.',
    /** The layout preference is per-user and server-side — it follows you to any browser. */
    layoutSaved: (layout: string): string => `Launcher layout set to ${layout}.`,
    brandingSaved: 'Branding updated — applies everywhere at the next page load.',
    brandingDiscarded: 'Unsaved branding changes discarded.',
    logoUpdated: 'Logo updated.',
    logoRemoved: 'Logo removed.',
    /** Read live from the settings table — no restart, unlike branding above. */
    operationalSettingsSaved: 'Operational settings updated — no restart needed.',
    operationalSettingsDiscarded: 'Unsaved operational settings discarded.',
    /** U5 — the same live-read rule as above: the next request is already counted by them. */
    rateLimitsSaved: 'Rate limits updated — the next request uses them.',
    rateLimitsDiscarded: 'Unsaved rate limit changes discarded.',
    accessSettingsSaved: 'Access settings updated.',
    emailSettingsSaved: 'Email settings updated.',
    emailSettingsDiscarded: 'Unsaved email settings discarded.',
    accessSettingsDiscarded: 'Unsaved access settings discarded.',
    /* ---- two-step ---- */
    twoStepOn: 'Two-step verification is on.',
    twoStepOff: 'Two-step is off — your password alone signs you in now.',
    newRecoveryCodes: 'New recovery codes are active — the old set no longer works.',
    /** The admin reset. The dialog stated the full consequences; this restates the sharpest. */
    twoFactorReset: (name: string): string =>
      `Two-step reset for ${name} — they are signed out everywhere.`,
  },
} as const;
